import React, { useState, useEffect, useCallback, useRef } from 'react';
import OrderLookupPanel from '../components/OrderLookupPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card, Table, Row, Col, Statistic, Tag, Typography, Descriptions, Tabs, Spin, Alert,
  Button, Space, Input, InputNumber, Select, Modal, Form, message, Tooltip, Avatar, Divider,
  Empty, List, DatePicker, Switch, Checkbox, Radio
} from 'antd';
import {
  UserOutlined, TeamOutlined, DollarOutlined, SafetyCertificateOutlined,
  LogoutOutlined, ReloadOutlined, EyeOutlined, PlusOutlined, EditOutlined,
  CopyOutlined, SearchOutlined, CrownOutlined, KeyOutlined,
  PhoneOutlined, BarChartOutlined, LinkOutlined, LockOutlined,
  CheckCircleOutlined, CloseCircleOutlined, MessageOutlined, WalletOutlined,
  SwapOutlined, SendOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { authAPI, agentAPI, manualRechargeAPI } from '../services/api';
import { getStoredToken } from '../utils/auth';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const DATE_FORMAT = 'YYYY-MM-DD';
// 账单/流水明细起止日期最大跨度（含起止两端）为 30 天，即天数差 ≤ 29，与后端 resolveRechargeDateRange 一致。
const MAX_DATE_RANGE_DAYS = 30;

// 人工代充(需求一·D): 客服端提示音 + 醒目提示(无外部资源)。
// WebAudio 蜂鸣(880Hz ~0.5s); document.title 闪烁(窗口重新 focus 即停)。
// 浏览器自动播放策略: AudioContext 首次需用户交互后才可发声, 客服在后台操作过即满足。
let rechargeAudioCtx = null;
function playRechargeBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!rechargeAudioCtx) rechargeAudioCtx = new AudioCtx();
    const ctx = rechargeAudioCtx;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) { /* ignore */ } }
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
    gain.gain.setValueAtTime(0.25, now + 1.85);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 2);
  } catch (e) { /* ignore */ }
}

let rechargeTitleTimer = null;
function flashRechargeTitle(alertText) {
  try {
    if (typeof document === 'undefined') return;
    if (rechargeTitleTimer) return; // 已在闪烁, 不重复
    const original = document.title;
    let toggled = false;
    rechargeTitleTimer = setInterval(() => {
      document.title = toggled ? original : `🔔 ${alertText}`;
      toggled = !toggled;
    }, 900);
    const stop = () => {
      if (rechargeTitleTimer) { clearInterval(rechargeTitleTimer); rechargeTitleTimer = null; }
      document.title = original;
      window.removeEventListener('focus', stop);
    };
    window.addEventListener('focus', stop);
  } catch (e) { /* ignore */ }
}

const STATUS_CONFIG = {
  1: { text: '活跃', color: 'green', icon: <CheckCircleOutlined /> },
  0: { text: '禁用', color: 'red', icon: <CloseCircleOutlined /> }
};

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) {
    return Promise.reject(new Error('empty copy text'));
  }

  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(value).catch(() => fallbackCopyText(value));
  }

  return fallbackCopyText(value);
}

function fallbackCopyText(text) {
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? resolve() : reject(new Error('copy command failed'));
    } catch (error) {
      document.body.removeChild(textarea);
      reject(error);
    }
  });
}

const getAgentLevel = (agent) => {
  const level = Number(agent?.agent_level || agent?.agentLevel || 0);
  if (level > 0) {
    return level;
  }
  if (agent?.agent_type === 'promoter' || agent?.agentType === 'promoter') {
    return 1;
  }
  if (agent?.agent_type === 'master' || agent?.agentType === 'master' || agent?.is_master_agent || agent?.isMasterAgent) {
    return 2;
  }
  return 3;
};

const getDashboardPath = (level) => {
  if (level === 1) {
    return '/agent/promoter-dashboard';
  }
  if (level === 2) {
    return '/agent/master-dashboard';
  }
  return '/agent/dashboard';
};

const getAgentRoleName = (level) => {
  if (level === 1) {
    return '1级总推';
  }
  if (level === 2) {
    return '2级总代';
  }
  return '3级代理';
};

// ---------------------------------------------------------------------------
// 人工代充(需求一): 会话状态与聊天面板
// ---------------------------------------------------------------------------
const RECHARGE_SESSION_STATUS = {
  0: { text: '进行中', color: 'processing' },
  1: { text: '已完成', color: 'success' },
  2: { text: '已取消', color: 'default' }
};

// mall_order.status: 0待完成 / 1已付未发 / 3完成 / 4取消(§10.2 订正口径)。
const MALL_ORDER_STATUS = {
  0: { text: '待完成', color: 'default' },
  1: { text: '已付款', color: 'processing' },
  3: { text: '已完成', color: 'success' },
  4: { text: '已取消', color: 'error' }
};

const renderMallOrderStatusTag = (status) => {
  const meta = MALL_ORDER_STATUS[Number(status)];
  return meta ? <Tag color={meta.color}>{meta.text}</Tag> : <Tag>-</Tag>;
};

// 点击复制(navigator.clipboard 优先, 失败回退 document.execCommand); 成功 message.success('已复制')。
function copyRechargeText(value) {
  const text = String(value == null ? '' : value);
  if (!text) {
    return;
  }
  const ok = () => message.success('已复制');
  const fallback = () => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      ok();
    } catch (e) {
      message.error('复制失败');
    }
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(ok, fallback);
  } else {
    fallback();
  }
}

// 可点击复制的文本(点击即复制, 阻断冒泡以免误触所在列表项的选中)。value 为空时降级为 '-'。
function CopyText({ value, children, strong, type, style }) {
  if (value == null || value === '') {
    return <Text type="secondary">-</Text>;
  }
  return (
    <Tooltip title="点击复制">
      <Text
        strong={strong}
        type={type}
        style={{ cursor: 'pointer', ...(style || {}) }}
        onClick={(e) => { e.stopPropagation(); copyRechargeText(value); }}
      >
        {children != null ? children : value}
      </Text>
    </Tooltip>
  );
}

// 需求(2026-08-09 定稿, 覆盖旧的"AOZ→角色ID / 平台币→账号ID 二选一"): 客服侧统一显眼展示玩家
//   账号名 + 账号ID(account_name + accountId), 二者均点击即复制;
//   角色ID(characterId) 仅 AOZ 会话(productType==='aoz' 且 characterId>0)以小灰字备查, 平台币会话不显示;
//   account_name/accountId 缺失时安全降级(不渲染空值/空括号)。
function RechargeIdentity({ record }) {
  const name = record?.accountName ? String(record.accountName) : '';
  const accountId = Number(record?.accountId || 0);
  const isAoz = String(record?.productType || '') === 'aoz';
  const characterId = Number(record?.characterId || 0);
  return (
    <span>
      {name
        ? <CopyText value={name} strong>{name}</CopyText>
        : <Text type="secondary">玩家</Text>}
      {accountId > 0 && (
        <CopyText value={accountId} style={{ marginLeft: 8 }}>账号ID:{accountId}</CopyText>
      )}
      {isAoz && characterId > 0 && (
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>角色ID:{characterId}</Text>
      )}
    </span>
  );
}

// 日期范围跨度守卫(与 MAX_DATE_RANGE_DAYS / 后端 resolveRechargeDateRange 一致)。
const makeSpanDisabledDate = (pickerDates) => (current) => {
  if (!current) {
    return false;
  }
  const [fromDate, toDate] = pickerDates || [];
  const tooLate = fromDate && current.diff(fromDate, 'days') > MAX_DATE_RANGE_DAYS - 1;
  const tooEarly = toDate && toDate.diff(current, 'days') > MAX_DATE_RANGE_DAYS - 1;
  return !!tooLate || !!tooEarly;
};

// 新版联系方式消息 marker(与后端/玩家端 pay-chat 冻结一致): content 以 [[CONTACT]] 开头, 紧跟紧凑 JSON。
//   { ch:'wechat'|'alipay', text:<=200, img:'/uploads/contact/<file>' }; text/img 至少一非空。
//   安全: 解析容错(非法即当普通文本); img 必须为 /uploads/contact/ 前缀本站相对路径, 否则丢弃(防 XSS/外链)。
const CONTACT_MARKER_PREFIX = '[[CONTACT]]';
function parseContactMarker(content) {
  if (typeof content !== 'string' || content.indexOf(CONTACT_MARKER_PREFIX) !== 0) {
    return null;
  }
  let obj;
  try {
    obj = JSON.parse(content.slice(CONTACT_MARKER_PREFIX.length));
  } catch (e) {
    return null;
  }
  if (!obj || typeof obj !== 'object') {
    return null;
  }
  const ch = obj.ch === 'alipay' ? 'alipay' : (obj.ch === 'wechat' ? 'wechat' : null);
  if (!ch) {
    return null;
  }
  const text = typeof obj.text === 'string' ? obj.text : '';
  let img = typeof obj.img === 'string' ? obj.img : '';
  // XSS 防护: 图片 src 使用前严格校验 /uploads/contact/ 前缀(仅接受本站上传目录相对路径)。
  if (img && !/^\/uploads\/contact\/[A-Za-z0-9._-]+$/.test(img)) {
    img = '';
  }
  if (!text && !img) {
    return null;
  }
  return { ch, text, img };
}

// 联系方式卡片: 文本一律 React 文本节点(绝不 innerHTML), 图片 src 已在 parseContactMarker 校验前缀。
function RechargeContactCard({ contact }) {
  const title = `联系方式 · ${contact.ch === 'alipay' ? '支付宝' : '微信'}`;
  return (
    <div style={{
      border: '1px solid rgba(37,99,235,0.35)',
      background: '#f8fafc',
      color: '#0f172a',
      borderRadius: 12,
      padding: '10px 12px',
      minWidth: 160,
      wordBreak: 'break-word'
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: '#2563eb' }}>{title}</div>
      {contact.text ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: contact.img ? 6 : 0 }}>
          <span style={{ userSelect: 'text', wordBreak: 'break-all' }}>{contact.text}</span>
          <Button size="small" onClick={() => copyRechargeText(contact.text)}>复制</Button>
        </div>
      ) : null}
      {contact.img ? (
        <img
          src={contact.img}
          alt="联系方式二维码"
          style={{ maxWidth: 200, display: 'block', borderRadius: 6, cursor: 'zoom-in', border: '1px solid rgba(148,163,184,0.3)' }}
          onClick={() => window.open(contact.img, '_blank', 'noopener,noreferrer')}
        />
      ) : null}
    </div>
  );
}

function RechargeMessageBubble({ msg }) {
  const isAgent = msg.senderType === 'agent';
  const isSystem = msg.senderType === 'system';
  if (isSystem) {
    return (
      <div style={{ textAlign: 'center', margin: '6px 0' }}>
        <span style={{ fontSize: 12, color: '#94a3b8', background: 'rgba(148,163,184,0.12)', padding: '2px 10px', borderRadius: 10 }}>{msg.content}</span>
      </div>
    );
  }
  // 联系方式消息(player/agent 侧)按发送方分侧渲染卡片, 而非原始 [[CONTACT]]{...} 文本。
  const contact = parseContactMarker(msg.content);
  if (contact) {
    return (
      <div style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
        <div style={{ maxWidth: '72%' }}>
          <RechargeContactCard contact={contact} />
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2, textAlign: isAgent ? 'right' : 'left' }}>
            {msg.createTime ? dayjs(msg.createTime).format('MM-DD HH:mm:ss') : ''}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
      <div style={{
        maxWidth: '72%',
        padding: '8px 12px',
        borderRadius: 12,
        background: isAgent ? '#2563eb' : 'rgba(148,163,184,0.18)',
        color: isAgent ? '#fff' : 'inherit',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap'
      }}>
        <div>{msg.content}</div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2, textAlign: 'right' }}>
          {msg.createTime ? dayjs(msg.createTime).format('MM-DD HH:mm:ss') : ''}
        </div>
      </div>
    </div>
  );
}

// 需求3: 联系方式渠道枚举与中文标签(与玩家端 pay-chat.html channelLabel / 后端 CONTACT_NOTE_CHANNELS 一致)。
const RECHARGE_CONTACT_CHANNELS = [
  { value: 'wechat', label: '微信' },
  { value: 'alipay', label: '支付宝' }
];
function rechargeChannelLabel(ch) {
  return ch === 'alipay' ? '支付宝' : (ch === 'wechat' ? '微信' : String(ch || ''));
}

// 需求(自定义备注): 隐藏上一轮"联系方式渠道备注(微信/支付宝下拉+账号+发送+按渠道备注)"整套 UI。
//   代码保留(state/handlers/后端端点均在), 仅不渲染。置回 true 即可一键恢复旧 UI。
const SHOW_LEGACY_CONTACT_UI = false;

// 客服聊天面板: SSE 下行 + 轮询兜底 + POST 上行 + 完成/取消。
function RechargeChatPanel({ session, onOrderChanged, quickReplies }) {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  // 新版"发送联系方式给玩家"组合器(取代旧 contact-notes UI): 渠道 + 文字 + 二维码图片。
  const [newContactChannel, setNewContactChannel] = useState('wechat');
  const [newContactText, setNewContactText] = useState('');
  const [newContactFile, setNewContactFile] = useState(null);
  const [newContactSending, setNewContactSending] = useState(false);
  const contactFileInputRef = useRef(null);
  const [peerTyping, setPeerTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const lastIdRef = useRef(0);
  const pollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const listRef = useRef(null);
  // 需求2.1 客服端返客弹框: 记录已弹过的 sessionNo, 保证每会话仅弹一次(跨会话切换不重复轰炸)。
  const shownReturningRef = useRef(new Set());
  const sessionNo = session?.sessionNo;
  // 需求3: 客服端联系方式(微信/支付宝)组合发送 + 备注(返客双方可见)。
  const [contactNotes, setContactNotes] = useState([]);
  const [contactChannel, setContactChannel] = useState('wechat');
  const [contactAccount, setContactAccount] = useState('');
  const [contactSending, setContactSending] = useState(false);
  // 每次"联系方式组合发送"成功后, 在消息区底部追加一条独立的【添加备注】动作行(纯前端, 不入库为消息), 与玩家端 appendNoteAction 对齐。
  const [noteActions, setNoteActions] = useState([]);
  const noteSeqRef = useRef(0);
  // 需求(自定义备注): 客服给该玩家起的自由文本备注(微信备注名式, side=agent, last-write-wins)。
  //   读取来源 = 会话列表 session.myRemark(后端 listAgentSessions 派生); 保存后本地即时更新, 8s 轮询兜底一致。
  const [myRemark, setMyRemark] = useState('');
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);

  const appendMessages = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) {
      return;
    }
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const merged = [...prev];
      for (const m of incoming) {
        if (m && !seen.has(m.id)) {
          merged.push(m);
          seen.add(m.id);
        }
      }
      merged.sort((a, b) => Number(a.id) - Number(b.id));
      lastIdRef.current = merged.reduce((max, m) => Math.max(max, Number(m.id) || 0), lastIdRef.current);
      return merged;
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!sessionNo) {
      return;
    }
    try {
      const resp = await manualRechargeAPI.getMessages(sessionNo, { since: lastIdRef.current, limit: 200 });
      if (resp.data.success) {
        appendMessages(resp.data.data?.messages || []);
      }
    } catch (error) {
      // 轮询兜底失败静默, 不打扰用户
    }
  }, [sessionNo, appendMessages]);

  // 需求3: 拉取本(客服,账号)双方联系方式备注。备注为辅助信息, 失败静默降级(不打扰主聊天流程)。
  const fetchContactNotes = useCallback(async () => {
    if (!sessionNo) {
      return;
    }
    try {
      const resp = await manualRechargeAPI.getContactNotes(sessionNo);
      if (resp.data.success) {
        setContactNotes(Array.isArray(resp.data.data?.notes) ? resp.data.data.notes : []);
      }
    } catch (error) {
      // 静默降级
    }
  }, [sessionNo]);

  useEffect(() => {
    setMessages([]);
    setPeerTyping(false);
    setConnected(false);
    lastIdRef.current = 0;
    if (!sessionNo) {
      return undefined;
    }
    fetchMessages();

    let es = null;
    try {
      const token = getStoredToken('agent');
      es = new EventSource(manualRechargeAPI.getAgentStreamUrl(sessionNo, token), { withCredentials: true });
      es.addEventListener('ready', () => setConnected(true));
      es.addEventListener('message', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.message) {
            appendMessages([data.message]);
            // 玩家新消息 -> 提示音 + 标题闪烁(自己/系统消息不提示)
            if (data.message.senderType === 'player') {
              playRechargeBeep();
              flashRechargeTitle('玩家新消息');
            }
          }
        } catch (error) { /* ignore parse */ }
      });
      es.addEventListener('typing', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.actor === 'player') {
            setPeerTyping(true);
            if (typingTimerRef.current) {
              clearTimeout(typingTimerRef.current);
            }
            typingTimerRef.current = setTimeout(() => setPeerTyping(false), 4000);
          }
        } catch (error) { /* ignore */ }
      });
      es.addEventListener('order', () => {
        if (onOrderChanged) {
          onOrderChanged();
        }
      });
      es.onerror = () => setConnected(false);
    } catch (error) {
      es = null;
    }

    pollRef.current = setInterval(fetchMessages, 5000);

    return () => {
      if (es) {
        try { es.close(); } catch (error) { /* ignore */ }
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [sessionNo, fetchMessages, appendMessages, onOrderChanged]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, peerTyping, noteActions]);

  // 需求3: 联系方式备注拉取/轮询(独立 effect, 与主 SSE 隔离; 会话切换即重置本地态, 玩家侧新增备注 8s 内可见)。
  useEffect(() => {
    setContactNotes([]);
    setNoteActions([]);
    setContactAccount('');
    if (!sessionNo) {
      return undefined;
    }
    fetchContactNotes();
    const timer = setInterval(fetchContactNotes, 8000);
    return () => clearInterval(timer);
  }, [sessionNo, fetchContactNotes]);

  // 需求(自定义备注): 从会话对象同步"本客服给该玩家的备注"。依赖仅基本类型(sessionNo + 备注文本),
  //   故 8s 轮询换 session 引用不会打断本地刚保存的值(保存后本地已 set, session.myRemark 会在下一轮追平)。
  useEffect(() => {
    setMyRemark(typeof session?.myRemark === 'string' ? session.myRemark : '');
  }, [sessionNo, session?.myRemark]);

  // 需求2.1 客服端返客弹框(§10.7): 独立 effect, 依赖仅基本类型 [sessionNo, session?.returning];
  // 绝不挂到每 8s 重建的 SSE effect, 也不依赖 session 对象; 仅客服主动选中会话时弹, 每会话一次。
  useEffect(() => {
    if (!sessionNo || session?.returning !== true) {
      return;
    }
    if (Number(session?.status) !== 0) {
      return; // 仅进行中会话提示
    }
    if (shownReturningRef.current.has(sessionNo)) {
      return;
    }
    shownReturningRef.current.add(sessionNo);
    Modal.info({
      title: '返客提醒',
      content: '你曾经给对方代充过，让他给你直接转账对应金额，你确认到账后直接点【订单完成】按钮。',
      okText: '我知道了'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionNo, session?.returning]);

  const closed = !session || session.status !== 0;

  const handleSend = async () => {
    const text = content.trim();
    if (!text || !sessionNo) {
      return;
    }
    // R2 快捷输入: 整段仅为 /N 或 \N(1-based 有序序号)时展开为该条快捷回复而非发送。越界仅告警。
    const shortcutMatch = text.match(/^[\/\\](\d+)$/);
    if (shortcutMatch) {
      const list = Array.isArray(quickReplies) ? quickReplies : [];
      const hit = list[parseInt(shortcutMatch[1], 10) - 1];
      if (hit) {
        setContent(hit.content);
      } else {
        message.warning(`无编号 ${shortcutMatch[1]} 的快捷回复`);
      }
      return;
    }
    if (text.length > 1000) {
      message.error('消息内容过长(上限1000字)');
      return;
    }
    setSending(true);
    try {
      const resp = await manualRechargeAPI.postMessage(sessionNo, { content: text });
      if (resp.data.success) {
        appendMessages([resp.data.data?.message].filter(Boolean));
        setContent('');
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (!sessionNo || closed) {
      return;
    }
    manualRechargeAPI.typing(sessionNo).catch(() => {});
  };

  // 新版联系方式发送: 有图先原始二进制上传拿 url, 再 POST /agent/sessions/:no/contact {channel,text,imageUrl}。
  //   text/图片至少一项; 成功后消息(marker)由返回值即时上屏, SSE/轮询亦会补齐。
  const handleSendNewContact = async () => {
    if (!sessionNo || closed) {
      return;
    }
    const text = newContactText.trim();
    const file = newContactFile;
    if (!text && !file) {
      message.error('请填写联系方式文字或上传二维码图片');
      return;
    }
    if (text.length > 200) {
      message.error('联系方式文字过长(上限200字)');
      return;
    }
    setNewContactSending(true);
    try {
      let imageUrl = '';
      if (file) {
        const up = await manualRechargeAPI.uploadContactImage(sessionNo, file);
        // 后端为嵌套信封 { success, message, data:{ url } }; 容错兼容扁平 { url }。
        const uploadedUrl = (up && up.data && up.data.url) || (up && up.url) || '';
        if (!up || !up.success || !uploadedUrl) {
          message.error(up && up.message ? up.message : '二维码图片上传失败');
          return;
        }
        imageUrl = uploadedUrl;
      }
      const resp = await manualRechargeAPI.sendContact(sessionNo, { channel: newContactChannel, text, imageUrl });
      if (resp.data.success) {
        const created = resp.data.data?.message || resp.data.message;
        appendMessages([created].filter(Boolean));
        setNewContactText('');
        setNewContactFile(null);
        if (contactFileInputRef.current) {
          contactFileInputRef.current.value = '';
        }
        message.success('联系方式已发送');
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setNewContactSending(false);
    }
  };

  // 需求3: 组合发送 —— 选定渠道+账号 => 作为一条"我用X，账号 Y"消息发给玩家, 并在消息区底部追加一条【添加备注】动作行。
  const handleSendContact = async () => {
    const account = contactAccount.trim();
    if (!sessionNo || closed) {
      return;
    }
    if (!account) {
      message.error('请填写你的微信/支付宝账号');
      return;
    }
    if (account.length > 128) {
      message.error('账号过长(上限128字)');
      return;
    }
    const channel = contactChannel;
    const text = `我用${rechargeChannelLabel(channel)}，账号 ${account}`;
    setContactSending(true);
    try {
      const resp = await manualRechargeAPI.postMessage(sessionNo, { content: text });
      if (resp.data.success) {
        appendMessages([resp.data.data?.message].filter(Boolean));
        const key = ++noteSeqRef.current;
        setNoteActions((prev) => [...prev, { key, channel, account, status: 'idle' }]);
        setContactAccount('');
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setContactSending(false);
    }
  };

  // 需求3: 点击【添加备注】=> POST /agent/.../contact-notes {channel, account}(后端 side 强制=agent, last-write-wins);
  //   成功变"✓ 已备注"并用返回的最新双方备注刷新展示区; 失败回退可重试。
  const handleAddNote = async (key) => {
    const target = noteActions.find((n) => n.key === key);
    if (!target || target.status === 'saving' || target.status === 'done') {
      return;
    }
    setNoteActions((prev) => prev.map((n) => (n.key === key ? { ...n, status: 'saving' } : n)));
    try {
      const resp = await manualRechargeAPI.saveContactNote(sessionNo, { channel: target.channel, account: target.account });
      if (resp.data.success) {
        setNoteActions((prev) => prev.map((n) => (n.key === key ? { ...n, status: 'done' } : n)));
        if (Array.isArray(resp.data.data?.notes)) {
          setContactNotes(resp.data.data.notes);
        }
        message.success('备注已保存，返客时双方可见');
      } else {
        setNoteActions((prev) => prev.map((n) => (n.key === key ? { ...n, status: 'idle' } : n)));
      }
    } catch (error) {
      setNoteActions((prev) => prev.map((n) => (n.key === key ? { ...n, status: 'idle' } : n)));
    }
  };

  // 需求(自定义备注): 打开编辑弹框(预填当前备注) / 保存(POST /agent/.../remark, side 后端强制=agent)。
  const openRemarkModal = () => {
    setRemarkDraft(myRemark || '');
    setRemarkModalOpen(true);
  };
  const handleSaveRemark = async () => {
    if (!sessionNo) {
      return;
    }
    const text = remarkDraft.trim();
    if (text.length > 200) {
      message.error('备注过长(上限200字)');
      return;
    }
    setRemarkSaving(true);
    try {
      const resp = await manualRechargeAPI.saveRemark(sessionNo, { remark: text });
      if (resp.data.success) {
        const saved = typeof resp.data.data?.my_remark === 'string' ? resp.data.data.my_remark : text;
        setMyRemark(saved);
        setRemarkModalOpen(false);
        message.success('备注已保存');
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setRemarkSaving(false);
    }
  };

  const handleComplete = () => {
    Modal.confirm({
      title: '确认完成订单',
      content: `将向玩家账号「${session.accountName}」入账 ¥${Number(session.amount).toFixed(2)} 平台币，并从你的余额(不足则向上级逐级)扣除等额。完成后不可撤销。`,
      okText: '确认完成',
      cancelText: '取消',
      onOk: async () => {
        setBusy(true);
        try {
          const resp = await manualRechargeAPI.complete(sessionNo);
          if (resp.data.success) {
            message.success('订单已完成入账');
            if (onOrderChanged) {
              onOrderChanged();
            }
          }
        } catch (error) {
          // 拦截器已提示(如余额不足)
        } finally {
          setBusy(false);
        }
      }
    });
  };

  const handleCancel = () => {
    let reason = '';
    Modal.confirm({
      title: '取消订单',
      content: (
        <div>
          <p>取消后不动任何钱包，会话将结束并保留记录。</p>
          <Input.TextArea rows={2} placeholder="取消原因(可选)" maxLength={255} onChange={(e) => { reason = e.target.value; }} />
        </div>
      ),
      okText: '确认取消',
      cancelText: '返回',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          const resp = await manualRechargeAPI.cancel(sessionNo, { reason });
          if (resp.data.success) {
            message.success('订单已取消');
            if (onOrderChanged) {
              onOrderChanged();
            }
          }
        } catch (error) {
          // 拦截器已提示
        } finally {
          setBusy(false);
        }
      }
    });
  };

  // 修改订单金额(客服): "玩家实付 < 下单金额"场景, 把本单金额改为实付金额后按新金额结算(到账 新金额×1.03)。
  //   客户端硬校验: 整数、大于0(与后端同口径); 仅平台币变体入口可见(AOZ 后端亦拒绝)。
  const handleUpdateAmount = () => {
    let nextAmount = Math.trunc(Number(session.amount) || 0);
    Modal.confirm({
      title: '修改订单金额',
      content: (
        <div>
          <p>用于「玩家实付少于下单金额」场景：改为玩家实际支付金额后点【完成订单】，本单即按新金额结算（到账 新金额×1.03，多送3%）。</p>
          <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>当前金额 ¥{Number(session.amount).toFixed(2)}</p>
          <InputNumber
            autoFocus
            style={{ width: '100%' }}
            min={1}
            max={10000}
            step={1}
            precision={0}
            defaultValue={nextAmount > 0 ? nextAmount : undefined}
            addonBefore="¥"
            placeholder="请输入新的金额(整数、大于0)"
            onChange={(v) => { nextAmount = v; }}
          />
        </div>
      ),
      okText: '确认修改',
      cancelText: '取消',
      onOk: async () => {
        const n = Number(nextAmount);
        // 客户端硬校验: 整数、大于0(与后端同口径, 双保险)。
        if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
          message.error('金额必须为大于0的整数');
          return Promise.reject(new Error('invalid amount'));
        }
        try {
          const resp = await manualRechargeAPI.updateAmount(sessionNo, { amount: n });
          if (resp.data.success) {
            message.success(`金额已修改为 ¥${Number(resp.data.data?.amount ?? n).toFixed(2)}`);
            if (onOrderChanged) {
              onOrderChanged();
            }
          }
        } catch (error) {
          // 拦截器已提示(如 AOZ 不支持 / 状态冲突 / 校验失败)
          return Promise.reject(error);
        }
      }
    });
  };

  if (!session) {
    return renderEmptyPanel('请选择左侧会话', '选择一个进行中的会话开始沟通与代充。');
  }

  const statusMeta = RECHARGE_SESSION_STATUS[session.status] || RECHARGE_SESSION_STATUS[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 520, border: '1px solid rgba(148,163,184,0.25)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(148,163,184,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          {/* 需求(自定义备注): 有备注则"备注为主 + 玩家账号(ID)为次"; 无备注则显示玩家账号(ID) + 醒目【添加备注】。 */}
          {myRemark ? (
            <>
              <Space size={8} wrap align="center">
                <Tooltip title="自定义备注（仅你可见，返客时优先显示）">
                  <Text strong style={{ fontSize: 16 }}>{myRemark}</Text>
                </Tooltip>
                <Button size="small" onClick={openRemarkModal}>编辑备注</Button>
                <Tag color={statusMeta.color}>{statusMeta.text}</Tag>
                {session.returning === true && <Tag color="gold">返客·曾代充过</Tag>}
                <Text type="secondary">¥{Number(session.amount).toFixed(2)}</Text>
              </Space>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                玩家 <RechargeIdentity record={session} /> · {session.channelKey}
              </div>
            </>
          ) : (
            <Space size={8} wrap align="center">
              <RechargeIdentity record={session} />
              <Button size="small" type="primary" ghost onClick={openRemarkModal}>＋ 添加备注</Button>
              <Tag color={statusMeta.color}>{statusMeta.text}</Tag>
              {session.returning === true && <Tag color="gold">返客·曾代充过</Tag>}
              <Text type="secondary">¥{Number(session.amount).toFixed(2)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{session.channelKey}</Text>
            </Space>
          )}
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            会话号 {session.sessionNo}{session.orderNo ? ` · 订单 ${session.orderNo}` : ''}
          </div>
        </div>
        <Space size={8} wrap>
          <span style={{ fontSize: 12, color: connected ? '#16a34a' : '#94a3b8' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: connected ? '#16a34a' : '#cbd5e1', marginRight: 4 }} />
            {connected ? '实时已连接' : '轮询模式'}
          </span>
          {!closed && (
            <>
              {session.productType !== 'aoz' && (
                <Button size="small" loading={busy} icon={<EditOutlined />} onClick={handleUpdateAmount}>修改金额</Button>
              )}
              <Button type="primary" size="small" loading={busy} icon={<CheckCircleOutlined />} onClick={handleComplete}>完成订单</Button>
              <Button danger size="small" loading={busy} icon={<CloseCircleOutlined />} onClick={handleCancel}>取消</Button>
            </>
          )}
        </Space>
      </div>

      {session.returning === true && (
        <Alert
          type="warning"
          showIcon
          banner
          message="返客提醒：你曾给该玩家代充过。让其直接转账对应金额，确认到账后点【完成订单】即可发货。"
        />
      )}

      {SHOW_LEGACY_CONTACT_UI && (() => {
        // 需求3(已隐藏): 顶部/返客区展示双方已存联系方式备注(我的=客服自己用过的号, 玩家的=玩家的号), 账号点击即复制。
        const list = Array.isArray(contactNotes) ? contactNotes : [];
        const agentItems = list.filter((n) => n && n.side === 'agent' && n.channel && n.account);
        const playerItems = list.filter((n) => n && n.side === 'player' && n.channel && n.account);
        if (!agentItems.length && !playerItems.length) {
          return null;
        }
        const renderItems = (items) => items.map((n, i) => (
          <span key={`${n.side}-${n.channel}-${i}`} style={{ marginRight: 14, whiteSpace: 'nowrap' }}>
            {rechargeChannelLabel(n.channel)}：
            <Tooltip title="点击复制">
              <span
                style={{ cursor: 'pointer', color: '#2563eb', borderBottom: '1px dotted #93c5fd' }}
                onClick={() => copyRechargeText(n.account)}
              >{n.account}</span>
            </Tooltip>
          </span>
        ));
        return (
          <div style={{ padding: '8px 14px', background: 'rgba(37,99,235,0.06)', borderBottom: '1px solid rgba(148,163,184,0.2)', fontSize: 13, lineHeight: 1.9 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>联系方式备注（返客双方可见 · 点击账号可复制）</div>
            {agentItems.length > 0 && <div><Text type="secondary">我的 </Text>{renderItems(agentItems)}</div>}
            {playerItems.length > 0 && <div><Text type="secondary">玩家的 </Text>{renderItems(playerItems)}</div>}
          </div>
        );
      })()}

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: 40 }}>暂无消息，主动向玩家打个招呼吧。</div>
        ) : messages.map((msg) => <RechargeMessageBubble key={msg.id} msg={msg} />)}
        {SHOW_LEGACY_CONTACT_UI && noteActions.map((na) => (
          <div key={na.key} style={{ display: 'flex', justifyContent: 'center', margin: '2px 0 10px' }}>
            {na.status === 'done' ? (
              <span style={{ color: '#16a34a', fontSize: 12 }}>
                ✓ 已备注：{rechargeChannelLabel(na.channel)} {na.account}（返客双方可见）
              </span>
            ) : (
              <Button
                size="small"
                loading={na.status === 'saving'}
                onClick={() => handleAddNote(na.key)}
                style={{ background: '#fff7e6', borderColor: '#ffd591', color: '#d46b08' }}
              >
                ＋ 添加备注（返客双方可见）
              </Button>
            )}
          </div>
        ))}
        {peerTyping && (
          <div style={{ fontSize: 12, color: '#94a3b8', margin: '4px 2px' }}>对方正在输入…</div>
        )}
      </div>

      {SHOW_LEGACY_CONTACT_UI && (
        <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148,163,184,0.2)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>发联系方式给玩家：</Text>
          <Select
            value={contactChannel}
            onChange={setContactChannel}
            disabled={closed || contactSending}
            style={{ width: 96 }}
            options={RECHARGE_CONTACT_CHANNELS}
          />
          <Input
            value={contactAccount}
            placeholder="填写你的微信/支付宝账号"
            maxLength={128}
            disabled={closed || contactSending}
            onChange={(e) => setContactAccount(e.target.value)}
            onPressEnter={handleSendContact}
            style={{ flex: '1 1 140px', minWidth: 120 }}
          />
          <Button type="primary" loading={contactSending} disabled={closed} onClick={handleSendContact}>发送联系方式</Button>
        </div>
      )}

      {!closed && (
        <>
          {/* 新版"发送联系方式给玩家"组合器: 渠道下拉 + 文字 + 二维码图片上传 + 发送。取代旧 contact-notes UI。 */}
          <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148,163,184,0.2)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>发送联系方式给玩家:</Text>
            <Select
              value={newContactChannel}
              onChange={setNewContactChannel}
              disabled={newContactSending}
              style={{ width: 96 }}
              options={RECHARGE_CONTACT_CHANNELS}
            />
            <Input
              value={newContactText}
              placeholder="联系方式文字（微信号/支付宝账号）"
              maxLength={200}
              disabled={newContactSending}
              onChange={(e) => setNewContactText(e.target.value)}
              onPressEnter={handleSendNewContact}
              style={{ flex: '1 1 140px', minWidth: 120 }}
            />
            <input
              ref={contactFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => setNewContactFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            />
            <Button
              size="small"
              disabled={newContactSending}
              onClick={() => { if (contactFileInputRef.current) { contactFileInputRef.current.click(); } }}
            >{newContactFile ? '重选二维码' : '上传二维码'}</Button>
            {newContactFile && (
              <Text type="secondary" style={{ fontSize: 12, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newContactFile.name}</Text>
            )}
            <Button type="primary" loading={newContactSending} onClick={handleSendNewContact}>发送</Button>
          </div>

          {/* 快捷回复下拉: 选中后把消息输入框内容整体替换为该条, select 自动复位占位。 */}
          <div style={{ padding: '6px 10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Select
              placeholder="快捷回复"
              value={undefined}
              style={{ flex: 1, minWidth: 120 }}
              disabled={sending}
              options={(Array.isArray(quickReplies) ? quickReplies : []).map((q, i) => ({ value: q.id, label: `${i + 1}. ${q.content}` }))}
              onChange={(id) => {
                const hit = (Array.isArray(quickReplies) ? quickReplies : []).find((q) => q.id === id);
                if (hit) {
                  setContent(hit.content);
                }
              }}
              notFoundContent="暂无快捷回复"
            />
          </div>
        </>
      )}

      <div style={{ padding: 10, borderTop: '1px solid rgba(148,163,184,0.2)' }}>
        {closed ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 6 }}>会话已结束（{statusMeta.text}），不可继续发送。</div>
        ) : (
          <Space.Compact style={{ display: 'flex', width: '100%' }}>
            <Input
              value={content}
              placeholder="输入消息，回车发送(上限1000字)"
              maxLength={1000}
              onChange={(e) => {
                const val = e.target.value;
                // R2 快捷输入: 整段为 /N + 尾随空白时(1-based 有序序号)展开为该条快捷回复; 越界则原样保留输入。
                const m = val.match(/^[\/\\](\d+)\s+$/);
                if (m) {
                  const list = Array.isArray(quickReplies) ? quickReplies : [];
                  const hit = list[parseInt(m[1], 10) - 1];
                  if (hit) {
                    setContent(hit.content);
                    return;
                  }
                }
                setContent(val);
              }}
              onFocus={handleTyping}
              onPressEnter={handleSend}
              disabled={sending}
            />
            <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleSend}>发送</Button>
          </Space.Compact>
        )}
      </div>

      {/* 需求(自定义备注): 客服给该玩家起自由文本备注(仅本人可见, 返客优先显示)。 */}
      <Modal
        title={myRemark ? '编辑备注' : '添加备注'}
        open={remarkModalOpen}
        onOk={handleSaveRemark}
        onCancel={() => setRemarkModalOpen(false)}
        confirmLoading={remarkSaving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>
          给该玩家起个自定义备注（如“老客户爱充1000”），仅你自己可见；返客时优先显示，玩家账号(ID) 作为次要信息。
        </div>
        <Input.TextArea
          value={remarkDraft}
          onChange={(e) => setRemarkDraft(e.target.value)}
          maxLength={200}
          showCount
          rows={3}
          placeholder="输入自定义备注（上限200字），留空并保存可清除备注"
        />
      </Modal>
    </div>
  );
}

function AgentDashboard({ expectedAgentLevel }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [agentData, setAgentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // 修复(收款方余额不自动刷新): 用 ref 镜像当前 Tab, 供 8s 后台轮询按需静默刷新钱包时判断当前是否在代充相关页,
  //   避免把 activeTab 加进轮询 effect 依赖导致每次切 Tab 都重建定时器/重置新会话检测种子。
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const [subAgents, setSubAgents] = useState([]);
  const [subAgentLoading, setSubAgentLoading] = useState(false);
  const [subAgentPagination, setSubAgentPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [subAgentKeyword, setSubAgentKeyword] = useState('');
  const [subAgentStats, setSubAgentStats] = useState({ totalSubAgents: 0, activeSubAgents: 0, totalSubUsers: 0 });
  const [flowDate, setFlowDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [flowSummaryAgents, setFlowSummaryAgents] = useState([]);
  const [flowSummaryLoading, setFlowSummaryLoading] = useState(false);

  const [playerKeyword, setPlayerKeyword] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState(null);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);

  const [rechargeLogs, setRechargeLogs] = useState([]);
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeLoaded, setRechargeLoaded] = useState(false);
  const [rechargeQuery, setRechargeQuery] = useState({ keyword: '', agentId: 0 });
  // 账单明细日期范围(起止两端，默认当天=单日语义，结果与改前一致)；rechargeRangePickerDates 用于跨度守卫。
  const [rechargeRange, setRechargeRange] = useState([dayjs(), dayjs()]);
  const [rechargeRangePickerDates, setRechargeRangePickerDates] = useState(null);
  const [rechargePagination, setRechargePagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [rechargeStats, setRechargeStats] = useState({ selectedDay: 0, period: 0 });

  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentSubAgent, setCurrentSubAgent] = useState(null);
  const [subAgentDetail, setSubAgentDetail] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const subAgentRequestRef = useRef(0);
  const flowSummaryRequestRef = useRef(0);
  const playerSearchRequestRef = useRef(0);
  const rechargeRequestRef = useRef(0);
  // 人工代充(需求一): 仅当 is_recharge_agent=1 才展示相关 Tab
  const [rechargeAccepting, setRechargeAccepting] = useState(false);
  const [rechargeSessions, setRechargeSessions] = useState([]);
  const [rechargeSessionsLoading, setRechargeSessionsLoading] = useState(false);
  const [selectedSessionNo, setSelectedSessionNo] = useState(null);
  const [rechargeWallet, setRechargeWallet] = useState(null);
  // 幽灵0根治: 区分"钱包尚未成功拉到(加载中/失败)"与"真实余额为0", 未加载前绝不渲染 ¥0
  const [walletLoaded, setWalletLoaded] = useState(false);
  const [walletError, setWalletError] = useState(false);
  // token/会话失效(401/403)判定位: 拦截器已在清 token 并跳登录, 此位用于让 3s/8s 钱包重试
  // 立即停手, 避免过期 token 在跳转窗口里反复打 401/403(区别于网络/5xx 抽风才持续重试)。
  const walletAuthFailedRef = useRef(false);
  const [transferVisible, setTransferVisible] = useState(false);
  const [transferForm] = Form.useForm();
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  // 补单(reissue): 受控弹窗(总价需随数量实时重算, 不能用 Modal.confirm 闭包)。
  const [reissueVisible, setReissueVisible] = useState(false);
  const [reissueSubmitting, setReissueSubmitting] = useState(false);
  const [reissueMode, setReissueMode] = useState('account'); // 'account' | 'accountId'
  const [reissueIdentifier, setReissueIdentifier] = useState('');
  const [reissueQuantity, setReissueQuantity] = useState(null);
  const [reissueRemark, setReissueRemark] = useState('');
  const [reissueRequestId, setReissueRequestId] = useState('');
  const rechargeSessionsRequestRef = useRef(0);
  // 新会话检测: 上一轮 status=0 会话号集合(null = 尚未播种, 首次不提示)
  const prevActiveSessionNosRef = useRef(null);
  // P1-2 多会话新消息提示: 每会话已见 lastMessageId 水位(Map, null = 尚未播种, 首次不误报) + 未读红点集合(纯前端)
  const seenMessageIdsRef = useRef(null);
  const [unreadSessions, setUnreadSessions] = useState(() => new Set());

  // 快捷回复(按客服私有, 无条数上限): 管理面板(会话列表附近)与对话框下拉共用同一份列表。
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickRepliesLoading, setQuickRepliesLoading] = useState(false);
  const [quickReplyPanelOpen, setQuickReplyPanelOpen] = useState(false);
  const [quickReplyDraft, setQuickReplyDraft] = useState('');
  const [quickReplyBusy, setQuickReplyBusy] = useState(false);
  // 编辑态: 正在编辑的快捷回复 id(null=无) 与其受控草稿文本(内联 Input)。
  const [quickReplyEditingId, setQuickReplyEditingId] = useState(null);
  const [quickReplyEditingText, setQuickReplyEditingText] = useState('');

  // 需求2.2: 我服务过的玩家(服务端受控分页) + 单玩家订单明细
  const [servedPlayers, setServedPlayers] = useState([]);
  const [servedPlayersLoading, setServedPlayersLoading] = useState(false);
  const [servedPlayersLoaded, setServedPlayersLoaded] = useState(false);
  const [servedPlayersKeyword, setServedPlayersKeyword] = useState('');
  const [servedPlayersPagination, setServedPlayersPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const servedPlayersRequestRef = useRef(0);
  const [servedOrdersVisible, setServedOrdersVisible] = useState(false);
  const [servedOrders, setServedOrders] = useState([]);
  const [servedOrdersLoading, setServedOrdersLoading] = useState(false);
  const [currentServedPlayer, setCurrentServedPlayer] = useState(null);

  // 需求3: 我的订单统计(客服维度)
  const [orderStatsRange, setOrderStatsRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
  const [orderStatsRangePickerDates, setOrderStatsRangePickerDates] = useState(null);
  const [orderStatsKeyword, setOrderStatsKeyword] = useState('');
  const [orderStatsSelfOnly, setOrderStatsSelfOnly] = useState(false);
  const [orderStatsData, setOrderStatsData] = useState(null);
  const [orderStatsLoading, setOrderStatsLoading] = useState(false);
  const [orderStatsLoaded, setOrderStatsLoaded] = useState(false);
  const orderStatsRequestRef = useRef(0);

  // 需求4: 通道转账 / 共享额度统计
  const [transferStatsRange, setTransferStatsRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
  const [transferStatsRangePickerDates, setTransferStatsRangePickerDates] = useState(null);
  const [transferStatsCounterparty, setTransferStatsCounterparty] = useState(0); // 0 = 全部对手方
  const [transferStatsType, setTransferStatsType] = useState('all'); // all | transfer | shared_quota
  const [transferStatsData, setTransferStatsData] = useState(null);
  const [transferStatsLoading, setTransferStatsLoading] = useState(false);
  const [transferStatsLoaded, setTransferStatsLoaded] = useState(false);
  const [transferStatsPagination, setTransferStatsPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const transferStatsRequestRef = useRef(0);

  const currentOrigin = window.location.origin;
  const loadedAgent = agentData?.agent || null;
  const loadedAgentId = Number(loadedAgent?.id || 0);
  const agentLevel = getAgentLevel(loadedAgent);
  const explicitManagePermission = loadedAgent?.can_manage_sub_agents ?? loadedAgent?.canManageSubAgents;
  const canManageSubAgents = Boolean(loadedAgent) && (explicitManagePermission === undefined
    ? agentLevel <= 2
    : explicitManagePermission === true || Number(explicitManagePermission) === 1);
  const agentRoleName = getAgentRoleName(agentLevel);
  const dashboardPath = getDashboardPath(agentLevel);
  const expectedPath = expectedAgentLevel ? getDashboardPath(expectedAgentLevel) : null;
  const childRoleName = agentLevel === 1 ? '2级总代' : '3级代理';
  const isRechargeAgent = Number(loadedAgent?.is_recharge_agent || 0) === 1;

  const loadRechargeSessions = useCallback(async (opts = {}) => {
    const silent = opts && opts.silent === true; // 后台 8s 轮询用 silent, 避免列表 loading 闪烁
    const requestId = ++rechargeSessionsRequestRef.current;
    if (!silent) {
      setRechargeSessionsLoading(true);
    }
    try {
      const response = await manualRechargeAPI.getSessions({ limit: 200 });
      if (requestId === rechargeSessionsRequestRef.current && response.data.success) {
        setRechargeSessions(response.data.data?.sessions || []);
      }
    } catch (error) {
      if (requestId === rechargeSessionsRequestRef.current) {
        console.error('Load recharge sessions error:', error);
      }
    } finally {
      if (!silent && requestId === rechargeSessionsRequestRef.current) {
        setRechargeSessionsLoading(false);
      }
    }
  }, []);

  const loadRechargeWallet = useCallback(async () => {
    try {
      const response = await manualRechargeAPI.getWallet({ limit: 30 });
      if (response.data && response.data.success && response.data.data) {
        setRechargeWallet(response.data.data);
        setWalletLoaded(true);
        setWalletError(false);
      } else {
        // 非成功信封(如 CDN 错误页 / success=false): 不清空已有值, 仅标记失败,
        // 避免把"没取到"渲染成 ¥0(客服看到假 0 余额是严重问题)
        setWalletError(true);
      }
    } catch (error) {
      console.error('Load recharge wallet error:', error);
      const status = error.response?.status;
      // token/会话失效(401/403): 全局拦截器已在清 token 并跳 /agent/login。此处不再画"加载失败"
      // (即将跳走, 免闪红), 并置位停止 3s/8s 重试, 杜绝过期 token 反复打 401/403。
      if (status === 401 || status === 403) {
        walletAuthFailedRef.current = true;
        return;
      }
      // 网络/CDN 抽风(无 response / 5xx / 非成功信封): 保留上次已知值, 标记失败; 由重试+占位符兜底, 绝不显示假 0
      setWalletError(true);
    }
  }, []);

  // 快捷回复: 拉取本客服私有列表。后端为嵌套信封 { success, message, data:{ items:[{id, content, sortOrder}] } }(已按序)。
  const loadQuickReplies = useCallback(async () => {
    setQuickRepliesLoading(true);
    try {
      const response = await manualRechargeAPI.getQuickReplies();
      if (response.data.success) {
        const payload = response.data.data || response.data; // 容错: 优先 data.items, 兜底扁平 items
        setQuickReplies(Array.isArray(payload.items) ? payload.items : []);
      }
    } catch (error) {
      // 辅助功能, 失败静默降级(不打扰主聊天流程)
    } finally {
      setQuickRepliesLoading(false);
    }
  }, []);

  // 快捷回复: 追加到末尾(后端 sort_order = max+1); 成功后刷新列表。
  const handleAddQuickReply = useCallback(async () => {
    const text = quickReplyDraft.trim();
    if (!text) {
      message.error('请输入快捷回复内容');
      return;
    }
    if (text.length > 1000) {
      message.error('内容过长(上限1000字)');
      return;
    }
    setQuickReplyBusy(true);
    try {
      const response = await manualRechargeAPI.addQuickReply({ content: text });
      if (response.data.success) {
        setQuickReplyDraft('');
        await loadQuickReplies();
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setQuickReplyBusy(false);
    }
  }, [quickReplyDraft, loadQuickReplies]);

  // 快捷回复: 删除自己的一条; 成功后刷新列表。
  const handleDeleteQuickReply = useCallback(async (id) => {
    setQuickReplyBusy(true);
    try {
      const response = await manualRechargeAPI.deleteQuickReply(id);
      if (response.data.success) {
        await loadQuickReplies();
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setQuickReplyBusy(false);
    }
  }, [loadQuickReplies]);

  // 快捷回复: 编辑单条内容(内联受控 Input)。校验照抄 add(空/超1000); 成功后关闭编辑态并刷新列表。响应嵌套 data.item。
  const handleEditQuickReply = useCallback(async (id, text) => {
    const content = (text || '').trim();
    if (!content) {
      message.error('请输入快捷回复内容');
      return;
    }
    if (content.length > 1000) {
      message.error('内容过长(上限1000字)');
      return;
    }
    setQuickReplyBusy(true);
    try {
      const response = await manualRechargeAPI.editQuickReply(id, { content });
      if (response.data.success) {
        setQuickReplyEditingId(null);
        setQuickReplyEditingText('');
        await loadQuickReplies();
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setQuickReplyBusy(false);
    }
  }, [loadQuickReplies]);

  // 快捷回复: 上移/下移(dir=-1/+1)。reorder body {ids} 必须恰为自己全部 reply id 集合, 故整表相邻交换后提交。
  const handleMoveQuickReply = useCallback(async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= quickReplies.length) {
      return;
    }
    const ids = quickReplies.map((q) => q.id);
    const tmp = ids[index];
    ids[index] = ids[target];
    ids[target] = tmp;
    setQuickReplyBusy(true);
    try {
      const response = await manualRechargeAPI.reorderQuickReplies({ ids });
      if (response.data.success) {
        await loadQuickReplies();
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setQuickReplyBusy(false);
    }
  }, [quickReplies, loadQuickReplies]);

  // P1-1: 稳定化传给 RechargeChatPanel 的 onOrderChanged 回调, 避免每次父渲染换引用导致选中会话的
  //   SSE effect 每 8s tear-down 重建(清屏/丢提示音)。依赖均为已 useCallback 的稳定加载器。
  const handleRechargeOrderChanged = useCallback(() => {
    loadRechargeSessions();
    loadRechargeWallet();
  }, [loadRechargeSessions, loadRechargeWallet]);

  // P1-2: 选中会话时清该会话未读红点, 并把已见 lastMessageId 水位同步到当前值(选中会话由其自身 SSE 提示, 不再由轮询重复响铃)。
  const selectRechargeSession = useCallback((sessionNo) => {
    setSelectedSessionNo(sessionNo);
    setUnreadSessions((prev) => {
      if (!prev.has(sessionNo)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(sessionNo);
      return next;
    });
    if (seenMessageIdsRef.current) {
      const sess = rechargeSessions.find((s) => s.sessionNo === sessionNo);
      if (sess) {
        seenMessageIdsRef.current.set(sessionNo, Number(sess.lastMessageId || 0));
      }
    }
  }, [rechargeSessions]);

  // 需求2.2: 服务过的玩家(服务端受控分页, 照 loadRechargeLogs 模板; resPage 出参 pagination:{page,limit,total})。
  const loadServedPlayers = useCallback(async ({ page = 1, pageSize = 20, keyword = '' } = {}) => {
    const requestId = ++servedPlayersRequestRef.current;
    setServedPlayersLoading(true);
    try {
      const response = await manualRechargeAPI.getServedPlayers({
        page,
        limit: pageSize,
        keyword: String(keyword || '').trim() || undefined
      });
      if (requestId === servedPlayersRequestRef.current && response.data.success) {
        const data = response.data.data || {};
        setServedPlayers(data.list || []);
        setServedPlayersPagination({
          current: Number(data.pagination?.page || page),
          pageSize: Number(data.pagination?.limit || pageSize),
          total: Number(data.pagination?.total || 0)
        });
        setServedPlayersLoaded(true);
      }
    } catch (error) {
      if (requestId === servedPlayersRequestRef.current) {
        console.error('Load served players error:', error);
      }
    } finally {
      if (requestId === servedPlayersRequestRef.current) {
        setServedPlayersLoading(false);
      }
    }
  }, []);

  // 需求3: 我的订单统计(客服维度)。self_only 仅 level2 有意义, level3 恒为本人。
  const loadOrderStats = useCallback(async ({ dateStart, dateEnd, keyword = '', selfOnly = false } = {}) => {
    const requestId = ++orderStatsRequestRef.current;
    setOrderStatsLoading(true);
    const startStr = (dateStart ? dayjs(dateStart) : dayjs()).format(DATE_FORMAT);
    const endStr = (dateEnd ? dayjs(dateEnd) : dayjs(startStr)).format(DATE_FORMAT);
    try {
      const response = await manualRechargeAPI.getOrderStats({
        date_start: startStr,
        date_end: endStr,
        keyword: String(keyword || '').trim() || undefined,
        self_only: selfOnly ? 1 : undefined,
        // 需求5: 拉取逐笔订单明细(每条订单展示玩家账号 + 账号ID)。低频业务, 单页取上限 100 条前端分页。
        page: 1,
        limit: 100
      });
      if (requestId === orderStatsRequestRef.current && response.data.success) {
        setOrderStatsData(response.data.data || null);
        setOrderStatsLoaded(true);
      }
    } catch (error) {
      if (requestId === orderStatsRequestRef.current) {
        console.error('Load order stats error:', error);
      }
    } finally {
      if (requestId === orderStatsRequestRef.current) {
        setOrderStatsLoading(false);
      }
    }
  }, []);

  // 需求4: 通道转账 / 共享额度借用统计(纯只读; 后端服务端收敛对手方范围)。
  const loadTransferStats = useCallback(async ({ page = 1, pageSize = 20, dateStart, dateEnd, counterparty = 0, type = 'all' } = {}) => {
    const requestId = ++transferStatsRequestRef.current;
    setTransferStatsLoading(true);
    const startStr = (dateStart ? dayjs(dateStart) : dayjs()).format(DATE_FORMAT);
    const endStr = (dateEnd ? dayjs(dateEnd) : dayjs(startStr)).format(DATE_FORMAT);
    try {
      const response = await manualRechargeAPI.getTransferStats({
        page,
        limit: pageSize,
        date_start: startStr,
        date_end: endStr,
        counterparty_agent_id: Number(counterparty) > 0 ? Number(counterparty) : undefined,
        type: type && type !== 'all' ? type : undefined
      });
      if (requestId === transferStatsRequestRef.current && response.data.success) {
        const data = response.data.data || {};
        setTransferStatsData(data);
        setTransferStatsPagination({
          current: Number(data.pagination?.page || page),
          pageSize: Number(data.pagination?.limit || pageSize),
          total: Number(data.pagination?.total ?? (Array.isArray(data.list) ? data.list.length : 0))
        });
        setTransferStatsLoaded(true);
      }
    } catch (error) {
      if (requestId === transferStatsRequestRef.current) {
        console.error('Load transfer stats error:', error);
      }
    } finally {
      if (requestId === transferStatsRequestRef.current) {
        setTransferStatsLoading(false);
      }
    }
  }, []);

  const handleToggleAccepting = useCallback(async (checked) => {
    try {
      const response = await manualRechargeAPI.setAccepting({ accepting: checked ? 1 : 0 });
      if (response.data.success) {
        setRechargeAccepting(Number(response.data.data?.acceptingOrders || 0) === 1);
        message.success(checked ? '已开启接单' : '已关闭接单');
      }
    } catch (error) {
      // 拦截器已提示
    }
  }, []);

  const loadAgentData = useCallback(async () => {
    setLoading(true);
    try {
      const agentResponse = await authAPI.getAgentMe();
      if (agentResponse.data.success) {
        setAgentData(agentResponse.data.data);
      }
    } catch (error) {
      console.error('Load agent data error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/agent/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadSubAgents = useCallback(async (page = 1, keyword = '', pageSize = 10) => {
    const requestId = ++subAgentRequestRef.current;
    setSubAgentLoading(true);
    try {
      const response = await agentAPI.getSubAgents({ page, limit: pageSize, keyword, date: flowDate });
      if (requestId === subAgentRequestRef.current && response.data.success) {
        setSubAgents(response.data.data.list);
        setSubAgentPagination(prev => ({
          ...prev,
          current: response.data.data.pagination.page,
          pageSize: Number(response.data.data.pagination.limit || pageSize),
          total: Number(response.data.data.pagination.total || 0)
        }));
      }
    } catch (error) {
      if (requestId === subAgentRequestRef.current) {
        console.error('Load sub-agents error:', error);
        message.error('加载下级代理列表失败');
      }
    } finally {
      if (requestId === subAgentRequestRef.current) {
        setSubAgentLoading(false);
      }
    }
  }, [flowDate]);

  const loadSubAgentStats = useCallback(async () => {
    try {
      const response = await agentAPI.getSubAgentStats();
      if (response.data.success) {
        setSubAgentStats(response.data.data);
      }
    } catch (error) {
      console.error('Load sub-agent stats error:', error);
    }
  }, []);

  const loadFlowSummary = useCallback(async (dateValue) => {
    const requestId = ++flowSummaryRequestRef.current;
    setFlowSummaryLoading(true);
    try {
      const response = await agentAPI.getSubAgents({
        page: 1,
        limit: 100,
        direct_only: 1,
        date: dateValue
      });
      if (requestId === flowSummaryRequestRef.current && response.data.success) {
        setFlowSummaryAgents(response.data.data?.list || []);
      }
    } catch (error) {
      if (requestId === flowSummaryRequestRef.current) {
        console.error('Load subordinate flow summary error:', error);
        message.error(error.response?.data?.message || '加载下级代理流水失败');
      }
    } finally {
      if (requestId === flowSummaryRequestRef.current) {
        setFlowSummaryLoading(false);
      }
    }
  }, []);

  const loadRechargeLogs = useCallback(async ({
    page = 1,
    pageSize = 20,
    keyword = '',
    agentId = 0,
    dateStart = rechargeRange[0],
    dateEnd = rechargeRange[1]
  } = {}) => {
    const requestId = ++rechargeRequestRef.current;
    setRechargeLoading(true);
    // 账单明细按日期范围查询：start=end 时即单日(与改前一致)。compactParams 会剔除空值。
    const startStr = (dateStart ? dayjs(dateStart) : dayjs()).format(DATE_FORMAT);
    const endStr = (dateEnd ? dayjs(dateEnd) : dayjs(startStr)).format(DATE_FORMAT);
    try {
      const response = await agentAPI.getRechargeLogs({
        page,
        limit: pageSize,
        keyword: String(keyword || '').trim() || undefined,
        agent_id: Number(agentId) > 0 ? Number(agentId) : undefined,
        date_start: startStr,
        date_end: endStr
      });
      if (requestId === rechargeRequestRef.current && response.data.success) {
        const data = response.data.data || {};
        setRechargeLogs(data.list || []);
        setRechargeStats(data.stats || { selectedDay: 0, period: 0 });
        setRechargePagination({
          current: Number(data.pagination?.current || page),
          pageSize: Number(data.pagination?.pageSize || pageSize),
          total: Number(data.pagination?.total || 0)
        });
        setRechargeLoaded(true);
      }
    } catch (error) {
      if (requestId === rechargeRequestRef.current) {
        console.error('Load recharge logs error:', error);
        message.error(error.response?.data?.message || '加载指定日期充值流水失败');
      }
    } finally {
      if (requestId === rechargeRequestRef.current) {
        setRechargeLoading(false);
      }
    }
  }, [rechargeRange]);

  const handleSearchPlayers = async (value) => {
    const keyword = String(value || '').trim();
    const requestId = ++playerSearchRequestRef.current;
    setPlayerKeyword(keyword);
    if (!keyword) {
      setPlayerSearchResults(null);
      setPlayerSearchLoading(false);
      return;
    }

    setPlayerSearchLoading(true);
    try {
      const response = await agentAPI.searchAccounts({ keyword, limit: 100 });
      if (requestId === playerSearchRequestRef.current && response.data.success) {
        setPlayerSearchResults(response.data.data?.list || []);
      }
    } catch (error) {
      if (requestId === playerSearchRequestRef.current) {
        console.error('Search players error:', error);
        message.error(error.response?.data?.message || '查询玩家归属失败');
      }
    } finally {
      if (requestId === playerSearchRequestRef.current) {
        setPlayerSearchLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAgentData();
  }, [loadAgentData]);

  useEffect(() => {
    if (!loadedAgent) {
      return;
    }

    if ((expectedPath && expectedPath !== dashboardPath) || location.pathname !== dashboardPath) {
      navigate(dashboardPath, { replace: true });
      return;
    }

    if (canManageSubAgents) {
      loadSubAgents(1, '', 10);
      loadSubAgentStats();
      loadFlowSummary(flowDate);
      return;
    }

    setSubAgents([]);
    setFlowSummaryAgents([]);
    setSubAgentStats({ totalSubAgents: 0, activeSubAgents: 0, totalSubUsers: 0 });
  }, [
    canManageSubAgents,
    dashboardPath,
    expectedPath,
    loadSubAgentStats,
    loadFlowSummary,
    loadSubAgents,
    flowDate,
    loadedAgentId,
    location.pathname,
    navigate
  ]);

  useEffect(() => {
    if (!canManageSubAgents && activeTab === 'sub-agents') {
      setActiveTab('overview');
    }
  }, [activeTab, canManageSubAgents]);

  useEffect(() => {
    if (loadedAgent && activeTab === 'recharge' && !rechargeLoaded) {
      loadRechargeLogs({
        page: 1,
        pageSize: rechargePagination.pageSize,
        keyword: rechargeQuery.keyword,
        agentId: rechargeQuery.agentId,
        dateStart: rechargeRange[0],
        dateEnd: rechargeRange[1]
      });
    }
  }, [
    activeTab,
    loadRechargeLogs,
    loadedAgent,
    loadedAgentId,
    rechargeLoaded,
    rechargePagination.pageSize,
    rechargeQuery.agentId,
    rechargeQuery.keyword,
    rechargeRange
  ]);

  // 人工代充: 同步接单开关初始值(来自 getAgentMe 的 accepting_orders)
  useEffect(() => {
    if (isRechargeAgent) {
      setRechargeAccepting(Number(loadedAgent?.accepting_orders || 0) === 1);
    }
  }, [isRechargeAgent, loadedAgent]);

  // 充值客服(is_recharge_agent=1)登录后默认落到"代充会话"(钱包卡+接单开关+进行中会话)工作视图,
  //   而非会误导的"总览"(推广三件套对客服恒为 0)。one-shot: 仅在数据加载后、用户尚未手动切走(仍在 overview)时
  //   生效一次; 之后客服可自由切换任意 Tab(含返回'总览', 此时三件套已隐藏, 不再误导)。非充值代理不受影响。
  const rechargeLandingDoneRef = useRef(false);
  useEffect(() => {
    if (isRechargeAgent && !rechargeLandingDoneRef.current && activeTab === 'overview') {
      rechargeLandingDoneRef.current = true;
      setActiveTab('manual-recharge');
    }
  }, [isRechargeAgent, activeTab]);

  // 人工代充: 进入相关 Tab 时按需加载会话与余额
  useEffect(() => {
    if (isRechargeAgent && (activeTab === 'manual-recharge' || activeTab === 'manual-orders')) {
      loadRechargeSessions();
      loadRechargeWallet();
    }
  }, [isRechargeAgent, activeTab, loadRechargeSessions, loadRechargeWallet]);

  // 快捷回复: 进入代充聊天 Tab 时加载本客服私有列表(管理面板与下拉共用)。
  useEffect(() => {
    if (isRechargeAgent && activeTab === 'manual-recharge') {
      loadQuickReplies();
    }
  }, [isRechargeAgent, activeTab, loadQuickReplies]);

  // 需求2.2: 首次进入"服务过的玩家" Tab 拉数据
  useEffect(() => {
    if (isRechargeAgent && activeTab === 'served-players' && !servedPlayersLoaded) {
      loadServedPlayers({ page: 1, pageSize: servedPlayersPagination.pageSize, keyword: servedPlayersKeyword });
    }
  }, [isRechargeAgent, activeTab, servedPlayersLoaded, loadServedPlayers, servedPlayersPagination.pageSize, servedPlayersKeyword]);

  // 需求3: 首次进入"我的订单统计" Tab 拉数据
  useEffect(() => {
    if (isRechargeAgent && activeTab === 'order-stats' && !orderStatsLoaded) {
      loadOrderStats({ dateStart: orderStatsRange[0], dateEnd: orderStatsRange[1], keyword: orderStatsKeyword, selfOnly: orderStatsSelfOnly });
    }
  }, [isRechargeAgent, activeTab, orderStatsLoaded, loadOrderStats, orderStatsRange, orderStatsKeyword, orderStatsSelfOnly]);

  // 需求4: 首次进入"转账统计" Tab 拉数据
  useEffect(() => {
    if (isRechargeAgent && activeTab === 'transfer-stats' && !transferStatsLoaded) {
      loadTransferStats({ page: 1, pageSize: transferStatsPagination.pageSize, dateStart: transferStatsRange[0], dateEnd: transferStatsRange[1], counterparty: transferStatsCounterparty, type: transferStatsType });
    }
  }, [isRechargeAgent, activeTab, transferStatsLoaded, loadTransferStats, transferStatsPagination.pageSize, transferStatsRange, transferStatsCounterparty, transferStatsType]);

  // 人工代充(需求一·D): 新会话轮询(8s), 无论当前在哪个 Tab 都能被通知(客服端无其它推送渠道)。
  //   修复(收款方余额不自动刷新): 在同一轮询里追加一次静默钱包刷新——被转入的收款方无需手动重载即可在数秒内
  //   看到余额增加。loadRechargeWallet 本身即静默语义(无 loading 态; 失败仅 console.error 不清空 -> 保留上次值),
  //   故无需额外 silent 参数。仅在钱包相关的代充客服页轮询, 非代充用户或其它页不受影响; 立即种子调用维持原样
  //   (余额首刷由"进入 Tab 按需加载"负责), 避免与既有刷新重复触发抖动。
  useEffect(() => {
    if (!isRechargeAgent) {
      return undefined;
    }
    // 代充客服工作页集合(其余如 overview/users/recharge/income/sub-agents 属管理页, 不轮询钱包)
    const RECHARGE_WALLET_TABS = new Set(['manual-recharge', 'manual-orders', 'served-players', 'order-stats', 'transfer-stats']);
    loadRechargeSessions({ silent: true }); // 立即拉一次(为新会话检测播种)
    const timer = setInterval(() => {
      loadRechargeSessions({ silent: true });
      if (RECHARGE_WALLET_TABS.has(activeTabRef.current) && !walletAuthFailedRef.current) {
        loadRechargeWallet(); // 静默刷新余额(含被转入的收款方); 会话已死则不再打(拦截器在跳登录)
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [isRechargeAgent, loadRechargeSessions, loadRechargeWallet]);

  // 幽灵0根治(接上): 钱包在"首次成功拉到"之前(网络/CDN抽风致失败), 每 3s 自动重试直到拿到真实值;
  // 加载成功即停止。拿到前 UI 显示占位而非 ¥0, 从根上杜绝客服看到假 0 余额。
  useEffect(() => {
    if (!isRechargeAgent || walletLoaded || walletAuthFailedRef.current) {
      return undefined;
    }
    loadRechargeWallet();
    const timer = setInterval(() => {
      // token/会话失效后停手(拦截器正在跳登录), 只对网络/5xx 抽风持续重试
      if (walletAuthFailedRef.current) {
        clearInterval(timer);
        return;
      }
      loadRechargeWallet();
    }, 3000);
    return () => clearInterval(timer);
  }, [isRechargeAgent, walletLoaded, loadRechargeWallet]);

  // 人工代充(需求一·D): diff 上一轮 status=0 会话集合, 发现新增进行中会话 -> 提示音 + 标题闪烁 + 高亮
  useEffect(() => {
    if (!isRechargeAgent) {
      return;
    }
    const activeNos = new Set(
      rechargeSessions.filter((s) => Number(s.status) === 0).map((s) => s.sessionNo)
    );
    const prev = prevActiveSessionNosRef.current;
    if (prev === null) {
      prevActiveSessionNosRef.current = activeNos; // 首次仅播种, 不提示
      return;
    }
    let hasNew = false;
    activeNos.forEach((no) => { if (!prev.has(no)) { hasNew = true; } });
    prevActiveSessionNosRef.current = activeNos;
    if (hasNew) {
      playRechargeBeep();
      flashRechargeTitle('新的代充会话');
      message.info('收到新的人工代充会话，请及时处理');
    }
  }, [rechargeSessions, isRechargeAgent]);

  // P1-2: 多会话新消息提示。8s 轮询带回每个进行中会话的 lastMessageId; diff 已见水位, 发现"非选中"进行中会话
  //   有新消息 -> 提示音 + 标题闪烁 + 该会话未读红点。当前选中会话由其自身 SSE 提示(不在此重复响铃);
  //   首次仅播种不误报; 新出现会话由既有"新会话"提示负责, 此处仅记录水位不重复响铃。
  useEffect(() => {
    if (!isRechargeAgent) {
      return;
    }
    const activeSessions = rechargeSessions.filter((s) => Number(s.status) === 0);
    if (seenMessageIdsRef.current === null) {
      const seed = new Map();
      activeSessions.forEach((s) => seed.set(s.sessionNo, Number(s.lastMessageId || 0)));
      seenMessageIdsRef.current = seed; // 首次仅播种
      return;
    }
    const seen = seenMessageIdsRef.current;
    const newlyUnread = [];
    activeSessions.forEach((s) => {
      const cur = Number(s.lastMessageId || 0);
      // 当前选中会话: 由其自身 SSE 负责提示, 这里只推进已读水位, 不响铃/不标红
      if (s.sessionNo === selectedSessionNo) {
        seen.set(s.sessionNo, cur);
        return;
      }
      // 新出现的会话(非选中): 记录水位, 响铃交由既有"新会话"提示, 此处不重复
      if (!seen.has(s.sessionNo)) {
        seen.set(s.sessionNo, cur);
        return;
      }
      if (cur > seen.get(s.sessionNo)) {
        newlyUnread.push(s.sessionNo);
        seen.set(s.sessionNo, cur);
      }
    });
    // 清理已不在进行中列表的水位, 避免 Map 无限增长
    const activeNoSet = new Set(activeSessions.map((s) => s.sessionNo));
    for (const key of Array.from(seen.keys())) {
      if (!activeNoSet.has(key)) {
        seen.delete(key);
      }
    }
    if (newlyUnread.length > 0) {
      setUnreadSessions((prev) => {
        const next = new Set(prev);
        newlyUnread.forEach((no) => next.add(no));
        return next;
      });
      playRechargeBeep();
      flashRechargeTitle('会话有新消息');
    }
  }, [rechargeSessions, isRechargeAgent, selectedSessionNo]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/agent/login', { replace: true });
  };

  const handleGenerateCode = async () => {
    if (!canManageSubAgents) {
      message.error('当前账号无权创建下级代理');
      return;
    }

    try {
      const response = await agentAPI.generateCode('SUB');
      createForm.setFieldValue('invite_code', response.data.data.invite_code);
      message.success('邀请码生成成功');
    } catch (error) {
      message.error('生成邀请码失败');
    }
  };

  const handleCreateSubAgent = async (values) => {
    if (!canManageSubAgents) {
      message.error('当前账号无权创建下级代理');
      return;
    }

    try {
      const response = await agentAPI.createSubAgent(values);
      if (response.data.success) {
        message.success(response.data.message || '下级代理创建成功');
        setCreateVisible(false);
        createForm.resetFields();
        loadSubAgents(1, subAgentKeyword, subAgentPagination.pageSize);
        loadSubAgentStats();
        if (response.data.data.login_info) {
          Modal.success({
            title: '下级代理创建成功',
            width: 480,
            content: (
              <div style={{ marginTop: 16 }}>
                <Paragraph>请妥善保管以下登录信息：</Paragraph>
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="登录账号">
                    <Text code>{response.data.data.login_info.username}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="登录密码">
                    <Text code>{response.data.data.login_info.password}</Text>
                  </Descriptions.Item>
                </Descriptions>
                <Alert
                  message="请将此信息发送给下级代理，密码仅显示一次"
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            )
          });
        }
      }
    } catch (error) {
      message.error(error.response?.data?.message || '创建下级代理失败');
    }
  };

  const handleEditSubAgent = (subAgent) => {
    if (!canManageSubAgents) {
      message.error('当前账号无权管理下级代理');
      return;
    }

    setCurrentSubAgent(subAgent);
    editForm.setFieldsValue({
      agent_name: subAgent.agent_name,
      contact: subAgent.contact,
      invite_code: subAgent.invite_code,
      commission_rate: subAgent.commission_rate,
      remark: subAgent.remark
    });
    setEditVisible(true);
  };

  const handleEditSubmit = async (values) => {
    if (!canManageSubAgents) {
      message.error('当前账号无权管理下级代理');
      return;
    }

    try {
      await agentAPI.updateSubAgent(currentSubAgent.id, values);
      message.success('下级代理信息更新成功');
      setEditVisible(false);
      editForm.resetFields();
      loadSubAgents(subAgentPagination.current, subAgentKeyword, subAgentPagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || '更新下级代理失败');
    }
  };

  const handleViewSubAgentDetail = async (subAgent) => {
    if (!canManageSubAgents) {
      message.error('当前账号无权查看下级代理');
      return;
    }

    try {
      const response = await agentAPI.getSubAgentDetail(subAgent.id, { date: flowDate });
      if (response.data.success) {
        setSubAgentDetail(response.data.data);
        setCurrentSubAgent(subAgent);
        setDetailVisible(true);
      }
    } catch (error) {
      message.error('加载下级代理详情失败');
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await copyTextToClipboard(code);
      message.success('邀请码已复制到剪贴板');
    } catch (error) {
      message.error('复制邀请码失败');
    }
  };

  const handleCopyRegisterLink = async (inviteCode) => {
    try {
      const registerUrl = `${window.location.origin}/register?inviteCode=${encodeURIComponent(inviteCode || '')}`;
      await copyTextToClipboard(registerUrl);
      message.success('注册链接已复制到剪贴板');
    } catch (error) {
      message.error('复制注册链接失败');
    }
  };

  const handleSearchSubAgents = (value) => {
    if (!canManageSubAgents) {
      return;
    }
    setSubAgentKeyword(value);
    loadSubAgents(1, value, subAgentPagination.pageSize);
  };

  const handleSearchRecharge = (value) => {
    const keyword = String(value || '').trim();
    const nextQuery = { ...rechargeQuery, keyword };
    setRechargeQuery(nextQuery);
    loadRechargeLogs({
      page: 1,
      pageSize: rechargePagination.pageSize,
      keyword,
      agentId: nextQuery.agentId,
      dateStart: rechargeRange[0],
      dateEnd: rechargeRange[1]
    });
  };

  const handleRechargeAgentChange = (value) => {
    const agentId = Number(value || 0);
    const nextQuery = { ...rechargeQuery, agentId };
    setRechargeQuery(nextQuery);
    loadRechargeLogs({
      page: 1,
      pageSize: rechargePagination.pageSize,
      keyword: nextQuery.keyword,
      agentId,
      dateStart: rechargeRange[0],
      dateEnd: rechargeRange[1]
    });
  };

  const handleRechargeTableChange = (pagination) => {
    loadRechargeLogs({
      page: pagination.current,
      pageSize: pagination.pageSize,
      keyword: rechargeQuery.keyword,
      agentId: rechargeQuery.agentId,
      dateStart: rechargeRange[0],
      dateEnd: rechargeRange[1]
    });
  };

  // 账单明细日期范围变更：前端守卫跨度<=30天(与后端 400 双校验)，合法后按新范围重查。
  const handleRechargeRangeChange = (values) => {
    if (!values || !values[0] || !values[1]) {
      return;
    }
    const [startValue, endValue] = values;
    if (endValue.diff(startValue, 'days') > MAX_DATE_RANGE_DAYS - 1) {
      message.error(`日期跨度不能超过 ${MAX_DATE_RANGE_DAYS} 天（含起止两端）`);
      return;
    }
    setRechargeRange([startValue, endValue]);
    loadRechargeLogs({
      page: 1,
      pageSize: rechargePagination.pageSize,
      keyword: rechargeQuery.keyword,
      agentId: rechargeQuery.agentId,
      dateStart: startValue,
      dateEnd: endValue
    });
  };

  const handleFlowDateChange = (value) => {
    const nextDate = (value || dayjs()).format('YYYY-MM-DD');
    setFlowDate(nextDate);
    setRechargeLoaded(false);
  };

  const handleOpenAgentFlowDetails = (subAgent) => {
    const agentId = Number(subAgent?.id || 0);
    if (!agentId) {
      return;
    }
    setRechargeQuery({ keyword: '', agentId });
    setRechargeLoaded(false);
    setActiveTab('recharge');
  };

  // ---- 需求2.2: 服务过的玩家 ----
  const handleServedPlayersTableChange = (pagination) => {
    loadServedPlayers({ page: pagination.current, pageSize: pagination.pageSize, keyword: servedPlayersKeyword });
  };

  const handleSearchServedPlayers = (value) => {
    const keyword = String(value || '').trim();
    setServedPlayersKeyword(keyword);
    loadServedPlayers({ page: 1, pageSize: servedPlayersPagination.pageSize, keyword });
  };

  const handleOpenServedPlayerOrders = async (player) => {
    const accountId = Number(player?.accountId || 0);
    if (!accountId) {
      return;
    }
    setCurrentServedPlayer(player);
    setServedOrders([]);
    setServedOrdersVisible(true);
    setServedOrdersLoading(true);
    try {
      const response = await manualRechargeAPI.getServedPlayerOrders(accountId, { limit: 200 });
      if (response.data.success) {
        // 明细为账号级(同一 account_id), 注入已知 accountId 以保证平台币行恒可显示"账号ID";
        // 后端若逐行返回 accountId/characterId 则以后端为准(展开在后覆盖)。
        const list = (response.data.data?.list || []).map((row) => ({ accountId, ...row }));
        setServedOrders(list);
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setServedOrdersLoading(false);
    }
  };

  // ---- 需求3: 我的订单统计 ----
  const handleQueryOrderStats = () => {
    loadOrderStats({ dateStart: orderStatsRange[0], dateEnd: orderStatsRange[1], keyword: orderStatsKeyword, selfOnly: orderStatsSelfOnly });
  };

  const handleOrderStatsRangeChange = (values) => {
    if (!values || !values[0] || !values[1]) {
      return;
    }
    const [startValue, endValue] = values;
    if (endValue.diff(startValue, 'days') > MAX_DATE_RANGE_DAYS - 1) {
      message.error(`日期跨度不能超过 ${MAX_DATE_RANGE_DAYS} 天（含起止两端）`);
      return;
    }
    setOrderStatsRange([startValue, endValue]);
    loadOrderStats({ dateStart: startValue, dateEnd: endValue, keyword: orderStatsKeyword, selfOnly: orderStatsSelfOnly });
  };

  const handleOrderStatsSelfOnlyChange = (event) => {
    const checked = event.target.checked;
    setOrderStatsSelfOnly(checked);
    loadOrderStats({ dateStart: orderStatsRange[0], dateEnd: orderStatsRange[1], keyword: orderStatsKeyword, selfOnly: checked });
  };

  // ---- 需求4: 转账统计 ----
  const handleQueryTransferStats = () => {
    loadTransferStats({ page: 1, pageSize: transferStatsPagination.pageSize, dateStart: transferStatsRange[0], dateEnd: transferStatsRange[1], counterparty: transferStatsCounterparty, type: transferStatsType });
  };

  const handleTransferStatsTableChange = (pagination) => {
    loadTransferStats({ page: pagination.current, pageSize: pagination.pageSize, dateStart: transferStatsRange[0], dateEnd: transferStatsRange[1], counterparty: transferStatsCounterparty, type: transferStatsType });
  };

  const handleTransferStatsRangeChange = (values) => {
    if (!values || !values[0] || !values[1]) {
      return;
    }
    const [startValue, endValue] = values;
    if (endValue.diff(startValue, 'days') > MAX_DATE_RANGE_DAYS - 1) {
      message.error(`日期跨度不能超过 ${MAX_DATE_RANGE_DAYS} 天（含起止两端）`);
      return;
    }
    setTransferStatsRange([startValue, endValue]);
    loadTransferStats({ page: 1, pageSize: transferStatsPagination.pageSize, dateStart: startValue, dateEnd: endValue, counterparty: transferStatsCounterparty, type: transferStatsType });
  };

  const handleTransferCounterpartyChange = (value) => {
    const counterparty = Number(value) || 0;
    setTransferStatsCounterparty(counterparty);
    loadTransferStats({ page: 1, pageSize: transferStatsPagination.pageSize, dateStart: transferStatsRange[0], dateEnd: transferStatsRange[1], counterparty, type: transferStatsType });
  };

  const handleTransferTypeChange = (value) => {
    setTransferStatsType(value);
    loadTransferStats({ page: 1, pageSize: transferStatsPagination.pageSize, dateStart: transferStatsRange[0], dateEnd: transferStatsRange[1], counterparty: transferStatsCounterparty, type: value });
  };

  if (loading) {
    return (
      <div className="drawer-loading" style={{ minHeight: 360 }}>
        <Spin size="large" tip="加载代理数据..." />
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="page-shell">
        <Alert
          message="加载失败"
          description="无法加载代理数据，请重新登录"
          type="error"
          showIcon
          action={<Button type="primary" onClick={() => navigate('/agent/login')}>返回登录</Button>}
        />
      </div>
    );
  }

  const { agent, users = [], incomeLogs = [], subAgents: mySubAgents = [] } = agentData;
  const visibleFlowAgents = Array.from(new Map(
    [agent, ...mySubAgents]
      .filter((item) => Number(item?.id) > 0)
      .map((item) => [Number(item.id), {
        ...item,
        id: Number(item.id),
        agent_level: getAgentLevel(item),
        agent_role_name: item.agent_role_name || getAgentRoleName(getAgentLevel(item))
      }])
  ).values()).sort((left, right) => left.agent_level - right.agent_level || left.id - right.id);
  const visibleAgentMetaMap = new Map(visibleFlowAgents.map((item) => [Number(item.id), item]));
  const displayedUsers = playerSearchResults === null ? users : playerSearchResults;
  const selectedFlowAgent = rechargeQuery.agentId > 0
    ? visibleAgentMetaMap.get(Number(rechargeQuery.agentId))
    : null;
  const flowDateLabel = dayjs(flowDate).format('YYYY-MM-DD');
  const flowDateShortLabel = dayjs(flowDate).format('MM-DD');
  // 账单明细日期范围标签(单日显示为一天，跨日显示 起 ~ 止)。
  const rechargeRangeStart = rechargeRange[0] || dayjs();
  const rechargeRangeEnd = rechargeRange[1] || rechargeRangeStart;
  const rechargeIsSingleDay = rechargeRangeStart.isSame(rechargeRangeEnd, 'day');
  const rechargeRangeLabel = rechargeIsSingleDay
    ? rechargeRangeStart.format(DATE_FORMAT)
    : `${rechargeRangeStart.format(DATE_FORMAT)} ~ ${rechargeRangeEnd.format(DATE_FORMAT)}`;
  const rechargeRangeShortLabel = rechargeIsSingleDay
    ? rechargeRangeStart.format('MM-DD')
    : `${rechargeRangeStart.format('MM-DD')} ~ ${rechargeRangeEnd.format('MM-DD')}`;
  const rechargeRangeValue = [rechargeRangeStart, rechargeRangeEnd];
  // 充值客服(is_recharge_agent=1): "绑定用户数/累计收益/充值总额"是【推广代理】口径(充值额按玩家的推广代理
  //   sys_user.agent_id 归集, 人工充值订单永不归接单客服名下), 对客服恒为 0 且严重误导 -> 一律不展示。
  //   客服真实业务见"代充会话/代充订单/我的订单统计/服务过的玩家"Tab 与钱包卡。非充值代理保持原样。
  const overviewStats = isRechargeAgent ? [] : [
    { title: '绑定用户数', value: `${agent.user_count || users.length} 人`, icon: <TeamOutlined />, color: '#2563eb' },
    { title: '累计收益', value: `¥${Number(agent.total_income || 0).toFixed(2)}`, icon: <DollarOutlined />, color: '#16a34a' },
    { title: '充值总额', value: `¥${Number(agent.total_sales ?? agent.total_recharge ?? 0).toFixed(2)}`, icon: <BarChartOutlined />, color: '#ea580c' }
  ];
  if (canManageSubAgents) {
    overviewStats.push({ title: '下级代理', value: `${subAgentStats.totalSubAgents} 个`, icon: <CrownOutlined />, color: '#7c3aed' });
  }

  const userColumns = [
    {
      title: '用户 ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: '用户名',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
      render: (text, record) => renderTableStack(text || '-', record.nick_name || '无昵称', record.game_name ? `角色 ${record.game_name}` : null)
    },
    {
      title: '昵称',
      dataIndex: 'nick_name',
      key: 'nick_name',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '游戏角色',
      dataIndex: 'game_name',
      key: 'game_name',
      width: 120,
      render: (text) => text ? <Tag color="blue">{text}</Tag> : '-'
    },
    {
      title: '等级',
      dataIndex: 'user_level',
      key: 'user_level',
      width: 80,
      render: (level) => level ? <Tag color="geekblue">Lv.{level}</Tag> : '-'
    },
    {
      title: '战力',
      dataIndex: 'power',
      key: 'power',
      width: 100,
      render: (power) => power ? Number(power).toLocaleString() : '-'
    },
    {
      title: 'VIP',
      dataIndex: 'vip_lv',
      key: 'vip_lv',
      width: 70,
      render: (vip) => vip ? <Tag color="gold">V{vip}</Tag> : '-'
    },
    {
      title: '所属代理',
      dataIndex: 'source_agent_name',
      key: 'source_agent_name',
      width: 150,
      render: (name, record) => {
        const owner = visibleAgentMetaMap.get(Number(record.source_agent_id));
        return renderTableStack(
          name || owner?.agent_name || '未找到代理',
          owner?.agent_role_name || (Number(record.source_agent_id) === Number(agent.id) ? agentRoleName : ''),
          record.source_agent_id ? `代理ID ${record.source_agent_id}` : null
        );
      }
    },
    {
      title: '区服',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 80,
      render: (serverId) => serverId ? `${serverId}服` : '-'
    },
    {
      title: '绑定时间',
      dataIndex: 'bind_time',
      key: 'bind_time',
      width: 170,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    }
  ];

  const incomeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70
    },
    {
      title: '收益金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>+¥{Number(amount).toFixed(2)}</Text>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    }
  ];

  const subAgentColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70
    },
    {
      title: '代理名称',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 140,
      render: (text, record) => renderTableStack(text, record.contact || '无联系方式', `用户 ${record.user_count || 0} / 下级 ${record.sub_agent_count || 0}`)
    },
    {
      title: '层级',
      dataIndex: 'agent_role_name',
      key: 'agent_role_name',
      width: 100,
      render: (_, record) => <Tag color={record.agent_level === 1 ? 'volcano' : record.agent_level === 2 ? 'gold' : 'blue'}>{record.agent_role_name || getAgentRoleName(getAgentLevel(record))}</Tag>
    },
    {
      title: '登录账号',
      dataIndex: 'username',
      key: 'username',
      width: 130,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: '邀请码',
      dataIndex: 'invite_code',
      key: 'invite_code',
      width: 180,
      render: (text) => (
        <Space size={4} wrap={false}>
          <Text code style={{ fontSize: 12 }}>{text}</Text>
          <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopyCode(text)} title="复制邀请码" style={{ padding: '0 2px' }} />
          <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => handleCopyRegisterLink(text)} title="复制注册链接" style={{ padding: '0 2px' }} />
        </Space>
      )
    },
    {
      title: '联系方式',
      dataIndex: 'contact',
      key: 'contact',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '佣金比例',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      width: 90,
      render: (rate) => (
        <Tag color="purple">{rate || 0}%</Tag>
      )
    },
    {
      title: '用户数',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 80,
      render: (count) => <Tag color="blue">{count || 0}人</Tag>
    },
    {
      title: '下级数',
      dataIndex: 'sub_agent_count',
      key: 'sub_agent_count',
      width: 80,
      render: (count) => count > 0 ? <Tag color="orange">{count}个</Tag> : '-'
    },
    {
      title: `${flowDateShortLabel} 团队流水`,
      dataIndex: 'selected_day_sales',
      key: 'selected_day_sales',
      width: 130,
      render: (amount, record) => <Text strong style={{ color: '#ea580c' }}>¥{Number(amount ?? record.selected_day_recharge_before ?? 0).toFixed(2)}</Text>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG[0];
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewSubAgentDetail(record)} />
          </Tooltip>
          {Number(record.parent_id) === Number(agent.id) ? (
            <Tooltip title="编辑直接下级">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditSubAgent(record)} />
            </Tooltip>
          ) : (
            <Tag>只读</Tag>
          )}
        </Space>
      )
    }
  ];

  const flowSummaryColumns = [
    {
      title: '层级',
      dataIndex: 'agent_role_name',
      key: 'agent_role_name',
      width: 110,
      render: (roleName, record) => (
        <Tag color={getAgentLevel(record) === 2 ? 'gold' : 'blue'}>
          {roleName || getAgentRoleName(getAgentLevel(record))}
        </Tag>
      )
    },
    {
      title: '代理账号',
      dataIndex: 'username',
      key: 'username',
      width: 170,
      render: (username, record) => renderTableStack(
        <Text code>{username || '-'}</Text>,
        record.agent_name || '-',
        `代理ID ${record.id}`
      )
    },
    {
      title: '直属玩家',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 100,
      render: (count) => `${Number(count || 0)} 人`
    },
    {
      title: '下级代理',
      dataIndex: 'sub_agent_count',
      key: 'sub_agent_count',
      width: 100,
      render: (count) => `${Number(count || 0)} 个`
    },
    {
      title: `${flowDateShortLabel} 团队流水`,
      dataIndex: 'selected_day_sales',
      key: 'selected_day_sales',
      width: 160,
      render: (amount, record) => <Text strong style={{ color: '#ea580c' }}>¥{Number(amount ?? record.selected_day_recharge_before ?? 0).toFixed(2)}</Text>
    },
    {
      title: '原返佣口径金额',
      dataIndex: 'selected_day_recharge_after',
      key: 'selected_day_recharge_after',
      width: 140,
      render: (amount) => <Text strong style={{ color: '#16a34a' }}>¥{Number(amount || 0).toFixed(2)}</Text>
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenAgentFlowDetails(record)}>
          逐笔明细
        </Button>
      )
    }
  ];

  const rechargeColumns = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 210,
      render: (text) => <Text code copyable={Boolean(text)}>{text || '-'}</Text>
    },
    {
      title: '玩家',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: (name, record) => renderTableStack(
        name || '-',
        record.nick_name || (record.character_name ? `角色 ${record.character_name}` : ''),
        record.character_id ? `角色ID ${record.character_id}` : null
      )
    },
    {
      title: '所属代理',
      dataIndex: 'source_agent_name',
      key: 'source_agent_name',
      width: 155,
      render: (name, record) => renderTableStack(
        name || '未找到代理',
        record.source_agent_role_name || '',
        record.source_agent_id ? `代理ID ${record.source_agent_id}` : null
      )
    },
    {
      title: '充值档位',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '实付金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 110,
      render: (amount) => <Text strong style={{ color: '#16a34a' }}>¥{Number(amount || 0).toFixed(2)}</Text>
    },
    {
      title: '支付方式',
      dataIndex: 'payment_provider',
      key: 'payment_provider',
      width: 130,
      render: (provider, record) => record.counts_cash === 0
        ? renderTableStack('客服代充', '仅充值展示，不新增返佣')
        : renderTableStack(provider || '-', record.payment_method || '')
    },
    {
      title: '区服',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 80,
      render: (serverId) => serverId ? `${serverId}服` : '-'
    },
    {
      title: '支付时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 175,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    }
  ];

  const renderOverviewTab = () => (
    <div className="detail-section">
      {!isRechargeAgent && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="充值总额和团队流水：在线支付＋客服代充，排除管理员补发及平台币消费。客服代充仅纳入充值展示，本次不新增返佣。" />}
      {canManageSubAgents && (
        <Card
          title={`直属${childRoleName}流水明细`}
          className="table-card"
          extra={(
            <Space wrap>
              <Text type="secondary">流水日期</Text>
              <DatePicker
                allowClear={false}
                value={dayjs(flowDate)}
                format="YYYY-MM-DD"
                onChange={handleFlowDateChange}
              />
              <Button size="small" onClick={() => handleFlowDateChange(dayjs())}>今天</Button>
              <Button size="small" onClick={() => handleFlowDateChange(dayjs().subtract(1, 'day'))}>昨天</Button>
            </Space>
          )}
        >
          <Alert
            type="info"
            showIcon
            message={`${agentRoleName}打开后直接显示直属${childRoleName}的 ${flowDateLabel} 流水`}
            description="团队流水包含该代理及下级玩家的在线支付与已确认客服代充；管理员补发、平台币消费、余额及推广币消费不计入。返佣仍按原现金口径独立计算。"
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={flowSummaryColumns}
            dataSource={flowSummaryAgents}
            loading={flowSummaryLoading}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 个直属下级` }}
            size="middle"
            scroll={{ x: 900 }}
            locale={{ emptyText: renderEmptyPanel('暂无下级代理流水', `${flowDateLabel} 没有可展示的直属下级代理。`) }}
          />
        </Card>
      )}

      {overviewStats.length > 0 && (
        <div className="compact-stat-row">
          {overviewStats.map((item) => (
            <div className="compact-stat" key={item.title}>
              <div className="table-cell">
                <div className="compact-stat-label">{item.title}</div>
                <div className="compact-stat-value" style={{ color: item.color }}>{item.value}</div>
                <div className="table-cell-secondary">{item.icon} 代理总览</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Card title={<span><SafetyCertificateOutlined style={{ marginRight: 8 }} />代理信息</span>} className="section-card">
        <Descriptions column={2} bordered size="middle" labelStyle={{ fontWeight: 600 }}>
          <Descriptions.Item label="代理ID">{agent.id}</Descriptions.Item>
          <Descriptions.Item label="代理名称"><Text strong>{agent.agent_name}</Text></Descriptions.Item>
          <Descriptions.Item label="登录账号"><Text code>{agent.username}</Text></Descriptions.Item>
          <Descriptions.Item label="当前层级">{agentRoleName}</Descriptions.Item>
          <Descriptions.Item label="邀请码">
            <Space size={4}>
              <Text code>{agent.invite_code}</Text>
              <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopyCode(agent.invite_code)} title="复制邀请码" style={{ padding: '0 2px' }} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="注册地址" span={2}>
            <Space>
              <Text code style={{ fontSize: 12 }}>{currentOrigin}/register?inviteCode={agent.invite_code}</Text>
              <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => handleCopyRegisterLink(agent.invite_code)} title="复制注册链接" style={{ padding: '0 2px' }} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="联系方式">{agent.contact || '-'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={agent.status === 1 ? 'green' : 'red'} icon={agent.status === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
              {agent.status === 1 ? '活跃' : '禁用'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间" span={2}>
            {agent.create_time ? new Date(agent.create_time).toLocaleString('zh-CN') : '-'}
          </Descriptions.Item>
          {agent.remark && (
            <Descriptions.Item label="备注" span={2}>{agent.remark}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {canManageSubAgents && mySubAgents.length > 0 && (
        <Card title={<span><CrownOutlined style={{ marginRight: 8, color: '#faad14' }} />下级代理概览</span>} className="section-card">
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
            dataSource={mySubAgents.slice(0, 8)}
            renderItem={(subAgent) => (
              <List.Item>
                <Card
                  hoverable
                  style={{ borderRadius: 12, border: '1px solid #f0f0f0' }}
                  onClick={() => handleViewSubAgentDetail(subAgent)}
                >
                  <div style={{ textAlign: 'center' }}>
                    <Avatar size={48} style={{ backgroundColor: subAgent.status === 1 ? '#52c41a' : '#ff4d4f', marginBottom: 8 }}>
                      {subAgent.agent_name?.charAt(0) || 'A'}
                    </Avatar>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{subAgent.agent_name}</div>
                    <Tag color={subAgent.status === 1 ? 'green' : 'red'} style={{ marginBottom: 4 }}>
                      {subAgent.status === 1 ? '活跃' : '禁用'}
                    </Tag>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      <TeamOutlined /> {subAgent.user_count || 0} 用户
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
          {mySubAgents.length > 8 && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Button type="link" onClick={() => setActiveTab('sub-agents')}>
                查看全部 {mySubAgents.length} 个下级代理 →
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );

  const renderUsersTab = () => (
    <Card
      className="table-card"
      title="玩家归属查询"
      extra={<Text type="secondary">当前显示 {displayedUsers.length} 个玩家</Text>}
    >
      <Alert
        type="info"
        showIcon
        message="查询范围由服务端按代理层级控制"
        description={agentLevel === 1
          ? '可查询自己及所属2级、3级代理的玩家，并显示玩家实际所属代理。'
          : agentLevel === 2
            ? '可查询自己及所属3级代理的玩家，无法查询1级总推或其他团队。'
            : '只能查询当前3级代理自己的玩家。'}
        style={{ marginBottom: 16 }}
      />
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="输入玩家账号、昵称、角色名或ID"
          style={{ width: 360 }}
          allowClear
          loading={playerSearchLoading}
          value={playerKeyword}
          onChange={(event) => setPlayerKeyword(event.target.value)}
          onSearch={handleSearchPlayers}
          enterButton="查询归属"
        />
        {playerSearchResults !== null && (
          <Button onClick={() => {
            setPlayerKeyword('');
            setPlayerSearchResults(null);
          }}>
            返回全部
          </Button>
        )}
      </Space>
      <Table
        columns={userColumns}
        dataSource={displayedUsers}
        loading={playerSearchLoading}
        rowKey={(record) => `${record.server_id || 0}-${record.user_id || 0}-${record.game_user_id || record.id || 0}`}
        pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 个用户`, showSizeChanger: true }}
        size="middle"
        scroll={{ x: 1200 }}
        locale={{ emptyText: renderEmptyPanel(
          playerSearchResults === null ? '暂无绑定用户' : '未找到玩家',
          playerSearchResults === null ? '当前权限范围内还没有绑定玩家。' : '当前权限范围内没有符合条件的玩家。'
        ) }}
      />
    </Card>
  );

  const renderRechargeTab = () => (
    <div className="detail-section">
      <OrderLookupPanel lookup={agentAPI.lookupOrders} agentScope />
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="充值流水已包含在线支付和已确认客服代充；排除管理员补发及平台币消费，返佣规则不变。" />
      <Alert
        type="info"
        showIcon
        message={`正在查看 ${rechargeRangeLabel} 的逐笔充值流水（最长 30 天，含起止两端）`}
        description={agentLevel === 1
          ? '可查看自己、所属2级代理和3级代理；可按具体代理、玩家和日期范围筛选。'
          : agentLevel === 2
            ? '可查看自己和所属3级代理；无法查看1级总推及其他团队。'
            : '只能查看当前3级代理自己在所选日期范围的流水。'}
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title={`${rechargeRangeShortLabel} 当前范围流水`} value={Number(rechargeStats.selectedDay || rechargeStats.period || 0)} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="当前范围订单数" value={rechargePagination.total} suffix="笔" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic
              title="当前查询范围"
              value={selectedFlowAgent?.agent_name || (canManageSubAgents ? '全部可见代理' : agent.agent_name)}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="table-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <RangePicker
            allowClear={false}
            value={rechargeRangeValue}
            format={DATE_FORMAT}
            style={{ width: 280 }}
            disabledDate={(current) => {
              if (!current) {
                return false;
              }
              const endpoints = rechargeRangePickerDates || [];
              const [fromDate, toDate] = endpoints;
              const tooLate = fromDate && current.diff(fromDate, 'days') > MAX_DATE_RANGE_DAYS - 1;
              const tooEarly = toDate && toDate.diff(current, 'days') > MAX_DATE_RANGE_DAYS - 1;
              return !!tooLate || !!tooEarly;
            }}
            onOpenChange={(open) => {
              setRechargeRangePickerDates(open ? [null, null] : null);
            }}
            onCalendarChange={(dates) => {
              setRechargeRangePickerDates(dates || [null, null]);
            }}
            onChange={handleRechargeRangeChange}
          />
          {canManageSubAgents && (
            <Select
              value={rechargeQuery.agentId}
              style={{ width: 260 }}
              onChange={handleRechargeAgentChange}
              options={[
                { value: 0, label: '全部可见代理' },
                ...visibleFlowAgents.map((item) => ({
                  value: item.id,
                  label: `${item.agent_role_name} · ${item.agent_name}（ID ${item.id}）`
                }))
              ]}
            />
          )}
          <Input.Search
            placeholder="充值流水内筛选（含客服代充）：玩家／订单号"
            style={{ width: 360 }}
            allowClear
            value={rechargeQuery.keyword}
            onChange={(event) => setRechargeQuery((previous) => ({ ...previous, keyword: event.target.value }))}
            onSearch={handleSearchRecharge}
            enterButton={<SearchOutlined />}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadRechargeLogs({
              page: rechargePagination.current,
              pageSize: rechargePagination.pageSize,
              keyword: rechargeQuery.keyword,
              agentId: rechargeQuery.agentId,
              dateStart: rechargeRange[0],
              dateEnd: rechargeRange[1]
            })}
          >
            刷新
          </Button>
          <Text type="secondary">查询范围：{rechargeRangeLabel}</Text>
        </Space>

        <Table
          columns={rechargeColumns}
          dataSource={rechargeLogs}
          loading={rechargeLoading}
          rowKey={(record) => String(record.id || record.order_no)}
          pagination={{
            current: rechargePagination.current,
            pageSize: rechargePagination.pageSize,
            total: rechargePagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${rechargeRangeLabel} 共 ${total} 笔订单`
          }}
          onChange={handleRechargeTableChange}
          size="middle"
          scroll={{ x: 1250 }}
          locale={{ emptyText: renderEmptyPanel('暂无充值流水', `${rechargeRangeLabel} 当前查询范围内没有符合条件的订单。`) }}
        />
      </Card>
    </div>
  );

  const renderIncomeTab = () => (
    <Card className="table-card" title="收益记录" extra={<Text type="secondary">共 {incomeLogs.length} 条收益流水</Text>}>
      <Table
        columns={incomeColumns}
        dataSource={incomeLogs}
        rowKey="id"
        pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 条记录`, showSizeChanger: true }}
        size="middle"
        locale={{ emptyText: renderEmptyPanel('暂无收益记录', '当前代理还没有可展示的收益流水。') }}
      />
    </Card>
  );

  const renderSubAgentsTab = () => (
    !canManageSubAgents ? (
      <Alert
        type="warning"
        showIcon
        message="当前账号无下级代理管理权限"
        description="3级代理只能查看自己的绑定玩家和收益流水。"
      />
    ) : (
    <div className="detail-section">
      <Row gutter={[16, 16]}>
        {[
          { title: '下级代理总数', value: subAgentStats.totalSubAgents, icon: <CrownOutlined />, color: '#722ed1' },
          { title: '活跃下级', value: subAgentStats.activeSubAgents, icon: <CheckCircleOutlined />, color: '#52c41a' },
          { title: '下级用户总数', value: subAgentStats.totalSubUsers, icon: <TeamOutlined />, color: '#1890ff' }
        ].map((item, index) => (
          <Col span={8} key={index}>
            <Card className="metric-card">
              <Statistic
                title={item.title}
                value={item.value}
                prefix={React.cloneElement(item.icon, { style: { color: item.color } })}
                valueStyle={{ color: item.color, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="table-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <DatePicker
            allowClear={false}
            value={dayjs(flowDate)}
            format="YYYY-MM-DD"
            onChange={handleFlowDateChange}
          />
          <Input.Search
            placeholder="搜索下级代理名称、邀请码、联系方式"
            style={{ width: 320 }}
            allowClear
            value={subAgentKeyword}
            onChange={(event) => {
              const value = event.target.value;
              setSubAgentKeyword(value);
              if (!value.trim()) {
                loadSubAgents(1, '', subAgentPagination.pageSize);
              }
            }}
            onSearch={handleSearchSubAgents}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateVisible(true);
            }}
          >
            创建{childRoleName}
          </Button>
        </Space>

        <Table
          columns={subAgentColumns}
          dataSource={subAgents}
          loading={subAgentLoading}
          rowKey="id"
          pagination={{
            ...subAgentPagination,
            showTotal: (total) => `共 ${total} 个下级代理`,
            showSizeChanger: true,
            onChange: (page, pageSize) => loadSubAgents(page, subAgentKeyword, pageSize)
          }}
          size="middle"
          locale={{ emptyText: renderEmptyPanel('暂无下级代理', '当前还没有创建下级代理。') }}
        />
      </Card>
    </div>
    )
  );

  // 人工代充: 派生会话分组与选中会话
  const activeRechargeSessions = rechargeSessions.filter((s) => Number(s.status) === 0);
  const orderRechargeSessions = rechargeSessions.filter((s) => Number(s.status) !== 0);
  const selectedRechargeSession = rechargeSessions.find((s) => s.sessionNo === selectedSessionNo) || null;

  const handleTransferSubmit = async (values) => {
    setTransferSubmitting(true);
    try {
      const requestId = `TRF_UI_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await manualRechargeAPI.transfer({
        toAgentId: values.toAgentId,
        amount: values.amount,
        remark: values.remark,
        requestId
      });
      if (response.data.success) {
        const balance = response.data.data?.fromBalance ?? response.data.data?.balance;
        message.success(`转账成功${balance !== undefined ? `，剩余余额 ¥${Number(balance).toFixed(2)}` : ''}`);
        setTransferVisible(false);
        transferForm.resetFields();
        loadRechargeWallet();
      }
    } catch (error) {
      // 拦截器已提示
    } finally {
      setTransferSubmitting(false);
    }
  };

  // 补单(reissue): 打开弹窗时生成一次性 requestId(幂等键, 满足 8-64位[A-Za-z0-9._:-]); prefill 可预填账号ID(从聊天面板入口)。
  const openReissueModal = (prefill) => {
    const rid = `RS${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setReissueRequestId(rid);
    if (prefill && prefill.accountId) {
      setReissueMode('accountId');
      setReissueIdentifier(String(prefill.accountId));
    } else {
      setReissueMode('account');
      setReissueIdentifier('');
    }
    setReissueQuantity(null);
    setReissueRemark('');
    setReissueVisible(true);
  };

  const handleReissueSubmit = async () => {
    const identifier = String(reissueIdentifier || '').trim();
    if (!identifier) {
      message.error(reissueMode === 'accountId' ? '请输入账号ID' : '请输入账号');
      return;
    }
    const qty = Number(reissueQuantity);
    // 前端硬校验: 整数、大于0(与后端同口径, 双保险)。
    if (!Number.isFinite(qty) || !Number.isInteger(qty) || qty <= 0) {
      message.error('购买数量必须为大于0的整数');
      return;
    }
    setReissueSubmitting(true);
    try {
      const resp = await manualRechargeAPI.reissue({
        identifier,
        quantity: qty,
        requestId: reissueRequestId,
        remark: reissueRemark
      });
      if (resp.data.success) {
        const d = resp.data.data || {};
        if (d.alreadyDone) {
          message.info('该补单已处理(幂等), 未重复发币');
        } else {
          message.success(`补单成功: 玩家「${d.accountName || identifier}」到账 ${d.delivered} 平台币(业绩计 ${d.totalAmount}）`);
        }
        setReissueVisible(false);
        loadRechargeWallet();
      }
    } catch (error) {
      // 拦截器已提示(如账号不存在 / 余额不足 / 校验失败)
    } finally {
      setReissueSubmitting(false);
    }
  };

  const reissueTotalPrice = (Number.isFinite(Number(reissueQuantity)) ? Number(reissueQuantity) : 0) * 1;

  // 幽灵0根治: 钱包三值统一渲染——未成功加载前显示"加载中/加载失败"占位(绝不显示 ¥0);
  // 只有真正拉到数据后才渲染金额(此时的 0 才是真实余额 0)。
  const renderWalletStat = (title, valueKey, color) => {
    if (!walletLoaded) {
      return (
        <Statistic
          title={title}
          value={walletError ? '加载失败·点刷新' : '加载中…'}
          valueStyle={{ color: walletError ? '#dc2626' : '#94a3b8', fontSize: 18 }}
        />
      );
    }
    return (
      <Statistic
        title={title}
        value={Number(rechargeWallet?.[valueKey] || 0)}
        precision={2}
        prefix="¥"
        valueStyle={color ? { color } : undefined}
      />
    );
  };

  const renderRechargeWalletBar = () => (
    <Card className="section-card" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={12} md={5}>
          {renderWalletStat('当前余额', 'balance', '#16a34a')}
        </Col>
        <Col xs={12} md={5}>
          {renderWalletStat('累计获得', 'totalGranted')}
        </Col>
        <Col xs={12} md={5}>
          {renderWalletStat('累计消耗', 'totalConsumed')}
        </Col>
        <Col xs={12} md={4}>
          <div style={{ marginBottom: 4, color: '#64748b', fontSize: 14 }}>接单开关</div>
          <Switch
            checked={rechargeAccepting}
            checkedChildren="接单中"
            unCheckedChildren="已停接"
            onChange={handleToggleAccepting}
          />
        </Col>
        <Col xs={24} md={5} style={{ textAlign: 'right' }}>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => { loadRechargeSessions(); loadRechargeWallet(); }}>刷新</Button>
            <Button icon={<PlusOutlined />} onClick={() => openReissueModal()}>补单</Button>
            <Button type="primary" icon={<SwapOutlined />} onClick={() => { transferForm.resetFields(); setTransferVisible(true); }}>转入下级</Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const renderManualRechargeTab = () => (
    <div className="detail-section">
      {renderRechargeWalletBar()}
      <Row gutter={16}>
        <Col xs={24} md={9} lg={8}>
          <Card
            className="table-card"
            title={`进行中会话 (${activeRechargeSessions.length})`}
            bodyStyle={{ padding: 0, maxHeight: 520, overflowY: 'auto' }}
            loading={rechargeSessionsLoading}
          >
            {activeRechargeSessions.length === 0 ? (
              renderEmptyPanel('暂无进行中会话', '玩家发起代充后会话会出现在这里。')
            ) : (
              <List
                dataSource={activeRechargeSessions}
                renderItem={(item) => {
                  const selected = item.sessionNo === selectedSessionNo;
                  const unread = unreadSessions.has(item.sessionNo);
                  return (
                    <List.Item
                      onClick={() => selectRechargeSession(item.sessionNo)}
                      style={{
                        cursor: 'pointer',
                        padding: '10px 16px',
                        background: selected ? 'rgba(37,99,235,0.1)' : 'transparent',
                        borderLeft: selected ? '3px solid #2563eb' : '3px solid transparent'
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>
                            {unread && (
                              <span
                                title="有新消息"
                                style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ef4444', marginRight: 6, verticalAlign: 'middle' }}
                              />
                            )}
                            <RechargeIdentity record={item} />
                            {item.returning === true && <Tag color="gold" style={{ marginLeft: 6 }}>返客</Tag>}
                          </span>
                          <Text style={{ color: '#ea580c' }}>¥{Number(item.amount).toFixed(2)}</Text>
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                          {item.channelKey} · {item.sessionNo}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>

          {/* 快捷回复管理面板(会话列表附近, 可折叠): 列表 + 上移/下移/删除 + 底部输入框添加, 无条数上限。 */}
          <Card
            className="table-card"
            style={{ marginTop: 16 }}
            title={(
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setQuickReplyPanelOpen((o) => !o)}
              >
                <span>快捷回复 ({quickReplies.length})</span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{quickReplyPanelOpen ? '收起 ▲' : '展开 ▼'}</span>
              </div>
            )}
            bodyStyle={{ padding: quickReplyPanelOpen ? 12 : 0 }}
          >
            {quickReplyPanelOpen && (
              <div>
                {quickRepliesLoading ? (
                  <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>
                ) : quickReplies.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>暂无快捷回复，在下方添加。</Text>
                ) : (
                  quickReplies.map((q, idx) => (
                    <div
                      key={q.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px solid rgba(148,163,184,0.15)' }}
                    >
                      {quickReplyEditingId === q.id ? (
                        <>
                          <Input.TextArea
                            value={quickReplyEditingText}
                            onChange={(e) => setQuickReplyEditingText(e.target.value)}
                            maxLength={1000}
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            disabled={quickReplyBusy}
                            style={{ flex: 1 }}
                          />
                          <Button size="small" type="primary" loading={quickReplyBusy} onClick={() => handleEditQuickReply(q.id, quickReplyEditingText)}>保存</Button>
                          <Button size="small" disabled={quickReplyBusy} onClick={() => { setQuickReplyEditingId(null); setQuickReplyEditingText(''); }}>取消</Button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 13, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{idx + 1}. {q.content}</span>
                          <Button size="small" disabled={quickReplyBusy} onClick={() => { setQuickReplyEditingId(q.id); setQuickReplyEditingText(q.content); }}>编辑</Button>
                          <Button size="small" disabled={idx === 0 || quickReplyBusy} onClick={() => handleMoveQuickReply(idx, -1)}>上移</Button>
                          <Button size="small" disabled={idx === quickReplies.length - 1 || quickReplyBusy} onClick={() => handleMoveQuickReply(idx, 1)}>下移</Button>
                          <Button size="small" danger disabled={quickReplyBusy} onClick={() => handleDeleteQuickReply(q.id)}>删除</Button>
                        </>
                      )}
                    </div>
                  ))
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <Input.TextArea
                    value={quickReplyDraft}
                    onChange={(e) => setQuickReplyDraft(e.target.value)}
                    maxLength={1000}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    placeholder="输入新的快捷回复内容"
                    disabled={quickReplyBusy}
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" loading={quickReplyBusy} onClick={handleAddQuickReply}>添加</Button>
                </div>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={15} lg={16}>
          <RechargeChatPanel
            session={selectedRechargeSession}
            onOrderChanged={handleRechargeOrderChanged}
            quickReplies={quickReplies}
          />
        </Col>
      </Row>
    </div>
  );

  const manualOrderColumns = [
    { title: '会话号', dataIndex: 'sessionNo', key: 'sessionNo', width: 190, render: (t) => <Text code>{t}</Text> },
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 200, render: (t) => t ? <Text code copyable>{t}</Text> : '-' },
    { title: '玩家账号', dataIndex: 'accountName', key: 'accountName', width: 150, render: (t) => <CopyText value={t} strong>{t || '-'}</CopyText> },
    // 定稿(2026-08-09): 统一显示账号ID(点击复制); 角色ID 仅 AOZ 以小灰字备查。
    {
      title: '玩家标识',
      key: 'identity',
      width: 160,
      render: (_, r) => {
        const accountId = Number(r?.accountId || 0);
        const isAoz = String(r?.productType || '') === 'aoz';
        const characterId = Number(r?.characterId || 0);
        return (
          <span>
            {accountId > 0 ? <CopyText value={accountId}>账号ID:{accountId}</CopyText> : <Text type="secondary">-</Text>}
            {isAoz && characterId > 0 && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>角色ID:{characterId}</Text></div>
            )}
          </span>
        );
      }
    },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 110, render: (a) => <Text strong style={{ color: '#16a34a' }}>¥{Number(a || 0).toFixed(2)}</Text> },
    // 增量(需求A): 该单发货完成后本客服代理币钱包余额; null(整单借上级/未扣本人) => '-'。
    { title: '完成后我的余额(元)', dataIndex: 'myBalanceAfter', key: 'myBalanceAfter', width: 160, render: (v) => (v == null ? '-' : `¥${Number(v).toFixed(2)}`) },
    { title: '渠道', dataIndex: 'channelKey', key: 'channelKey', width: 140, render: (t) => t || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const meta = RECHARGE_SESSION_STATUS[status] || RECHARGE_SESSION_STATUS[0];
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '取消原因', dataIndex: 'cancelReason', key: 'cancelReason', width: 160, render: (t) => t || '-' },
    { title: '完成/更新时间', dataIndex: 'finishedAt', key: 'finishedAt', width: 175, render: (t, r) => { const v = t || r.updateTime; return v ? new Date(v).toLocaleString('zh-CN') : '-'; } }
  ];

  const walletLogColumns = [
    { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 175, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' },
    { title: '类型', dataIndex: 'changeType', key: 'changeType', width: 130, render: (t) => t || '-' },
    // 需求1(2026-08-17): 余额流水显示被充值/相关玩家的账号 + 玩家ID(充值类由后端 JOIN 订单表带出; 非玩家行 admin_grant/内部划拨显示 '-')。
    { title: '玩家账号', dataIndex: 'playerAccountName', key: 'playerAccountName', width: 150, render: (t) => (t ? <CopyText value={t} strong>{t}</CopyText> : <Text type="secondary">-</Text>) },
    { title: '玩家ID', dataIndex: 'playerAccountId', key: 'playerAccountId', width: 120, render: (t) => (Number(t) > 0 ? <CopyText value={t}><Text code>{t}</Text></CopyText> : <Text type="secondary">-</Text>) },
    { title: '变动', dataIndex: 'changeAmount', key: 'changeAmount', width: 120, render: (a) => { const n = Number(a || 0); return <Text style={{ color: n >= 0 ? '#16a34a' : '#dc2626' }}>{n >= 0 ? '+' : ''}¥{n.toFixed(2)}</Text>; } },
    { title: '余额', dataIndex: 'balanceAfter', key: 'balanceAfter', width: 120, render: (a) => `¥${Number(a || 0).toFixed(2)}` },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true, render: (t) => t || '-' }
  ];

  const renderManualOrdersTab = () => (
    <div className="detail-section">
      {renderRechargeWalletBar()}
      <Card className="table-card" title={`代充订单记录 (${orderRechargeSessions.length})`} style={{ marginBottom: 16 }}>
        <Table
          columns={manualOrderColumns}
          dataSource={orderRechargeSessions}
          loading={rechargeSessionsLoading}
          rowKey="sessionNo"
          pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 条`, showSizeChanger: true }}
          size="middle"
          scroll={{ x: 1260 }}
          locale={{ emptyText: renderEmptyPanel('暂无订单记录', '完成或取消的会话会出现在这里。') }}
        />
      </Card>
      <Card className="table-card" title="余额流水">
        <Table
          columns={walletLogColumns}
          dataSource={rechargeWallet?.logs || []}
          rowKey="id"
          pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 条` }}
          size="middle"
          scroll={{ x: 970 }}
          locale={{ emptyText: renderEmptyPanel('暂无余额流水', '加余额、完成订单、转账都会在此记录。') }}
        />
      </Card>
    </div>
  );

  // ---- 需求2.2: 服务过的玩家 Tab ----
  const servedPlayerColumns = [
    { title: '玩家账号', dataIndex: 'accountName', key: 'accountName', width: 170, render: (t) => <CopyText value={t} strong>{t || '-'}</CopyText> },
    { title: '账号ID', dataIndex: 'accountId', key: 'accountId', width: 130, render: (t) => (Number(t) > 0 ? <CopyText value={t}><Text code>{t}</Text></CopyText> : <Text type="secondary">-</Text>) },
    { title: '最近服务时间', dataIndex: 'lastServedAt', key: 'lastServedAt', width: 185, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' },
    { title: '服务次数', dataIndex: 'servedCount', key: 'servedCount', width: 110, render: (n) => `${Number(n || 0)} 次` },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenServedPlayerOrders(record)}>订单明细</Button>
    }
  ];

  // 单玩家订单明细列(全部状态; 每行按 product_type 显示对应 ID 并注明)。
  const servedOrderColumns = [
    { title: '会话号', dataIndex: 'sessionNo', key: 'sessionNo', width: 180, render: (t) => <Text code>{t}</Text> },
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 200, render: (t) => t ? <Text code>{t}</Text> : '-' },
    {
      title: '玩家标识',
      key: 'identity',
      width: 160,
      render: (_, r) => {
        const accountId = Number(r?.accountId || 0);
        const isAoz = String(r?.productType || '') === 'aoz';
        const characterId = Number(r?.characterId || 0);
        return (
          <span>
            {accountId > 0 ? <CopyText value={accountId}>账号ID:{accountId}</CopyText> : <Text type="secondary">-</Text>}
            {isAoz && characterId > 0 && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>角色ID:{characterId}</Text></div>
            )}
          </span>
        );
      }
    },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 110, render: (a) => <Text strong style={{ color: '#16a34a' }}>¥{Number(a || 0).toFixed(2)}</Text> },
    // 增量(需求A): 该单发货完成后本客服代理币钱包余额; null(整单借上级/未扣本人) => '-'。
    { title: '完成后我的余额(元)', dataIndex: 'myBalanceAfter', key: 'myBalanceAfter', width: 160, render: (v) => (v == null ? '-' : `¥${Number(v).toFixed(2)}`) },
    { title: '渠道', dataIndex: 'channelKey', key: 'channelKey', width: 130, render: (t) => t || '-' },
    {
      title: '会话状态',
      dataIndex: 'sessionStatus',
      key: 'sessionStatus',
      width: 110,
      render: (s) => { const m = RECHARGE_SESSION_STATUS[s] || RECHARGE_SESSION_STATUS[0]; return <Tag color={m.color}>{m.text}</Tag>; }
    },
    { title: '订单状态', dataIndex: 'orderStatus', key: 'orderStatus', width: 110, render: (s) => renderMallOrderStatusTag(s) },
    { title: '取消原因', dataIndex: 'cancelReason', key: 'cancelReason', width: 150, render: (t) => t || '-' },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 175, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' },
    { title: '完成时间', dataIndex: 'finishedAt', key: 'finishedAt', width: 175, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' }
  ];

  const renderServedPlayersTab = () => (
    <div className="detail-section">
      <Alert
        type="info"
        showIcon
        message="我服务过的玩家"
        description="仅展示由你本人接单成功服务过的玩家(严格私有, 上级/其他客服不可见)。点击“订单明细”查看该玩家在你名下的全部状态订单。"
        style={{ marginBottom: 16 }}
      />
      <Card className="table-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder="搜索玩家账号或账号ID"
            style={{ width: 320 }}
            allowClear
            value={servedPlayersKeyword}
            onChange={(event) => setServedPlayersKeyword(event.target.value)}
            onSearch={handleSearchServedPlayers}
            enterButton={<SearchOutlined />}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadServedPlayers({ page: servedPlayersPagination.current, pageSize: servedPlayersPagination.pageSize, keyword: servedPlayersKeyword })}
          >
            刷新
          </Button>
        </Space>
        <Table
          columns={servedPlayerColumns}
          dataSource={servedPlayers}
          loading={servedPlayersLoading}
          rowKey={(record) => String(record.accountId)}
          pagination={{
            current: servedPlayersPagination.current,
            pageSize: servedPlayersPagination.pageSize,
            total: servedPlayersPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 位玩家`
          }}
          onChange={handleServedPlayersTableChange}
          size="middle"
          scroll={{ x: 720 }}
          locale={{ emptyText: renderEmptyPanel('暂无服务过的玩家', '完成代充订单后, 玩家会出现在这里。') }}
        />
      </Card>
    </div>
  );

  // ---- 需求3: 我的订单统计 Tab ----
  const orderStatsByAgentColumns = [
    {
      title: '客服',
      dataIndex: 'agentName',
      key: 'agentName',
      render: (t, r) => renderTableStack(t || `代理#${r.agentId}`, r.roleName || '', r.agentId ? `ID ${r.agentId}` : null)
    },
    { title: '成功订单数', dataIndex: 'orderCount', key: 'orderCount', width: 130, render: (n) => `${Number(n || 0)} 笔` },
    { title: '成功金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 160, render: (a) => <Text strong style={{ color: '#16a34a' }}>¥{Number(a || 0).toFixed(2)}</Text> }
  ];

  // 需求5: 订单统计——逐笔订单明细列(每条订单显示玩家账号 accountName + 账号ID accountId)。
  const orderStatsOrderColumns = [
    { title: '完成时间', dataIndex: 'finishedAt', key: 'finishedAt', width: 175, render: (t, r) => { const v = t || r.createTime; return v ? new Date(v).toLocaleString('zh-CN') : '-'; } },
    { title: '玩家账号', dataIndex: 'accountName', key: 'accountName', width: 160, render: (t) => <CopyText value={t} strong>{t || '-'}</CopyText> },
    { title: '账号ID', dataIndex: 'accountId', key: 'accountId', width: 120, render: (v) => (Number(v) > 0 ? <CopyText value={v}>{v}</CopyText> : <Text type="secondary">-</Text>) },
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 200, render: (t) => t ? <Text code copyable>{t}</Text> : '-' },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 110, render: (a) => <Text strong style={{ color: '#16a34a' }}>¥{Number(a || 0).toFixed(2)}</Text> },
    { title: '接单客服', dataIndex: 'agentName', key: 'agentName', width: 150, render: (t, r) => t || (r.agentId ? `代理#${r.agentId}` : '-') },
    { title: '渠道', dataIndex: 'channelKey', key: 'channelKey', width: 130, render: (t) => t || '-' }
  ];

  const renderOrderStatsTab = () => (
    <div className="detail-section">
      <Alert
        type="info"
        showIcon
        message="我的订单统计(客服维度)"
        description={agentLevel === 2
          ? '默认统计本人及全部下级客服的成功订单; 勾选"仅显示本人"只看自己。此为按接单客服归属的独立只读聚合, 与业绩归属互不影响。'
          : '统计当前客服本人在所选日期范围内的成功订单(按接单客服归属)。'}
        style={{ marginBottom: 16 }}
      />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="成功订单总金额" value={Number(orderStatsData?.totalAmount || 0)} precision={2} prefix="¥" valueStyle={{ color: '#16a34a' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="成功订单数" value={Number(orderStatsData?.orderCount || 0)} suffix="笔" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="统计范围" value={(agentLevel === 2 && !orderStatsSelfOnly) ? '本人 + 下级' : '仅本人'} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
      </Row>
      <Card className="table-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <RangePicker
            allowClear={false}
            value={orderStatsRange}
            format={DATE_FORMAT}
            style={{ width: 280 }}
            disabledDate={makeSpanDisabledDate(orderStatsRangePickerDates)}
            onOpenChange={(open) => setOrderStatsRangePickerDates(open ? [null, null] : null)}
            onCalendarChange={(dates) => setOrderStatsRangePickerDates(dates || [null, null])}
            onChange={handleOrderStatsRangeChange}
          />
          <Input.Search
            placeholder="搜索玩家账号/ID/订单号"
            style={{ width: 300 }}
            allowClear
            value={orderStatsKeyword}
            onChange={(event) => setOrderStatsKeyword(event.target.value)}
            onSearch={handleQueryOrderStats}
            enterButton={<SearchOutlined />}
          />
          {agentLevel === 2 && (
            <Checkbox checked={orderStatsSelfOnly} onChange={handleOrderStatsSelfOnlyChange}>仅显示本人</Checkbox>
          )}
          <Button type="primary" icon={<SearchOutlined />} loading={orderStatsLoading} onClick={handleQueryOrderStats}>查询</Button>
        </Space>
        {agentLevel === 2 && !orderStatsSelfOnly && (
          <Table
            columns={orderStatsByAgentColumns}
            dataSource={orderStatsData?.byAgent || []}
            loading={orderStatsLoading}
            rowKey={(record) => String(record.agentId)}
            pagination={false}
            size="middle"
            scroll={{ x: 520 }}
            locale={{ emptyText: renderEmptyPanel('暂无数据', '所选范围内没有成功订单。') }}
          />
        )}
        {/* 需求5: 逐笔订单明细(每条订单展示玩家账号 + 账号ID)。 */}
        <div style={{ marginTop: (agentLevel === 2 && !orderStatsSelfOnly) ? 16 : 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>订单明细（每条订单含玩家账号与账号ID）</div>
          <Table
            columns={orderStatsOrderColumns}
            dataSource={orderStatsData?.orders || []}
            loading={orderStatsLoading}
            rowKey={(record) => String(record.id)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="middle"
            scroll={{ x: 1000 }}
            locale={{ emptyText: renderEmptyPanel('暂无订单', '所选范围内没有成功订单。') }}
          />
          {Number(orderStatsData?.ordersPagination?.total || 0) > (orderStatsData?.orders?.length || 0) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              明细最多展示 100 条，当前范围共 {Number(orderStatsData?.ordersPagination?.total || 0)} 笔，请缩小日期范围或用搜索精确定位。
            </Text>
          )}
        </div>
      </Card>
    </div>
  );

  // ---- 需求4: 转账统计 Tab ----
  const TRANSFER_TYPE_META = {
    transfer: { text: '显式转账', color: 'blue' },
    shared_quota: { text: '共享额度借用', color: 'purple' }
  };
  const transferStatsColumns = [
    { title: '时间', dataIndex: 'time', key: 'time', width: 175, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (t) => { const m = TRANSFER_TYPE_META[t] || { text: t || '-', color: 'default' }; return <Tag color={m.color}>{m.text}</Tag>; }
    },
    { title: '出方', key: 'from', width: 175, render: (_, r) => renderTableStack(r.fromAgentName || `代理#${r.fromAgentId}`, null, r.fromAgentId ? `ID ${r.fromAgentId}` : null) },
    { title: '入方', key: 'to', width: 175, render: (_, r) => renderTableStack(r.toAgentName || `代理#${r.toAgentId}`, null, r.toAgentId ? `ID ${r.toAgentId}` : null) },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 130, render: (a) => <Text strong>¥{Number(a || 0).toFixed(2)}</Text> },
    { title: '关联订单', dataIndex: 'ref', key: 'ref', width: 200, render: (t) => t ? <Text code>{t}</Text> : '-' }
  ];

  const renderTransferStatsTab = () => (
    <div className="detail-section">
      <Alert
        type="info"
        showIcon
        message="转账 / 共享额度统计"
        description={agentLevel === 2
          ? '统计你与下级代理之间的显式转账, 以及下级借用你共享额度的记录。可按对手方与类型筛选。'
          : '统计你与所属通道管理员(上级)之间的显式转账, 以及你借用其共享额度的记录。对手方固定为你的通道管理员。'}
        style={{ marginBottom: 16 }}
      />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="显式转账合计" value={Number(transferStatsData?.summary?.transferTotal || 0)} precision={2} prefix="¥" valueStyle={{ color: '#2563eb' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="共享额度借用合计" value={Number(transferStatsData?.summary?.sharedQuotaTotal || 0)} precision={2} prefix="¥" valueStyle={{ color: '#7c3aed' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="metric-card">
            <Statistic title="记录数" value={Number(transferStatsData?.summary?.count ?? transferStatsPagination.total ?? 0)} suffix="条" />
          </Card>
        </Col>
      </Row>
      <Card className="table-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <RangePicker
            allowClear={false}
            value={transferStatsRange}
            format={DATE_FORMAT}
            style={{ width: 280 }}
            disabledDate={makeSpanDisabledDate(transferStatsRangePickerDates)}
            onOpenChange={(open) => setTransferStatsRangePickerDates(open ? [null, null] : null)}
            onCalendarChange={(dates) => setTransferStatsRangePickerDates(dates || [null, null])}
            onChange={handleTransferStatsRangeChange}
          />
          {agentLevel === 2 ? (
            <Select
              value={transferStatsCounterparty}
              style={{ width: 240 }}
              onChange={handleTransferCounterpartyChange}
              options={[
                { value: 0, label: '全部下级' },
                ...mySubAgents
                  .filter((s) => Number(s?.id) > 0)
                  .map((s) => ({ value: Number(s.id), label: `${s.agent_name}（ID ${s.id}）` }))
              ]}
            />
          ) : (
            <Tag color="gold">对手方：所属通道管理员</Tag>
          )}
          <Select
            value={transferStatsType}
            style={{ width: 170 }}
            onChange={handleTransferTypeChange}
            options={[
              { value: 'all', label: '全部类型' },
              { value: 'transfer', label: '显式转账' },
              { value: 'shared_quota', label: '共享额度借用' }
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={transferStatsLoading} onClick={handleQueryTransferStats}>查询</Button>
        </Space>
        <Table
          columns={transferStatsColumns}
          dataSource={transferStatsData?.list || []}
          loading={transferStatsLoading}
          rowKey={(record, index) => `${record.type}-${record.ref}-${record.fromAgentId}-${record.toAgentId}-${index}`}
          pagination={{
            current: transferStatsPagination.current,
            pageSize: transferStatsPagination.pageSize,
            total: transferStatsPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={handleTransferStatsTableChange}
          size="middle"
          scroll={{ x: 1000 }}
          locale={{ emptyText: renderEmptyPanel('暂无转账/借用记录', '所选范围内没有记录。') }}
        />
      </Card>
    </div>
  );

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <BarChartOutlined style={{ marginRight: 4 }} />
          总览
        </span>
      ),
      children: renderOverviewTab()
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 4 }} />
          绑定用户 ({users.length})
        </span>
      ),
      children: renderUsersTab()
    },
    {
      key: 'recharge',
      label: (
        <span>
          <BarChartOutlined style={{ marginRight: 4 }} />
          流水明细
        </span>
      ),
      children: renderRechargeTab()
    },
    {
      key: 'income',
      label: (
        <span>
          <DollarOutlined style={{ marginRight: 4 }} />
          收益记录 ({incomeLogs.length})
        </span>
      ),
      children: renderIncomeTab()
    }
  ];

  if (canManageSubAgents) {
    tabItems.push({
      key: 'sub-agents',
      label: (
        <span>
          <CrownOutlined style={{ marginRight: 4 }} />
          {agentLevel === 1 ? '下级代理（2/3级）' : '下级3级代理'} ({subAgentStats.totalSubAgents})
        </span>
      ),
      children: renderSubAgentsTab()
    });
  }

  if (isRechargeAgent) {
    tabItems.push({
      key: 'manual-recharge',
      label: (
        <span>
          <MessageOutlined style={{ marginRight: 4 }} />
          代充会话 ({activeRechargeSessions.length})
        </span>
      ),
      children: renderManualRechargeTab()
    });
    tabItems.push({
      key: 'manual-orders',
      label: (
        <span>
          <WalletOutlined style={{ marginRight: 4 }} />
          代充订单
        </span>
      ),
      children: renderManualOrdersTab()
    });
    tabItems.push({
      key: 'served-players',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 4 }} />
          服务过的玩家
        </span>
      ),
      children: renderServedPlayersTab()
    });
    tabItems.push({
      key: 'order-stats',
      label: (
        <span>
          <BarChartOutlined style={{ marginRight: 4 }} />
          我的订单统计
        </span>
      ),
      children: renderOrderStatsTab()
    });
    tabItems.push({
      key: 'transfer-stats',
      label: (
        <span>
          <SwapOutlined style={{ marginRight: 4 }} />
          转账统计
        </span>
      ),
      children: renderTransferStatsTab()
    });
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">旭日之战 {agentRoleName}后台</div>
          <div className="page-subtitle">
            {canManageSubAgents ? '查看当前权限范围内的代理团队、绑定玩家和充值流水。' : '查看当前代理自己的绑定玩家和收益流水。'}
          </div>
        </div>
        <div className="page-header-actions agent-dashboard-actions">
          <Tag color={agentLevel === 1 ? 'volcano' : agentLevel === 2 ? 'gold' : 'blue'} icon={<CrownOutlined />}>{agentRoleName}</Tag>
          <Tag color="gold" icon={<CrownOutlined />}>{agent.agent_name}</Tag>
          <Tag color={agent.status === 1 ? 'green' : 'red'}>{agent.status === 1 ? '活跃' : '禁用'}</Tag>
          <Button
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
          >
            退出登录
          </Button>
        </div>
      </div>

      <div className="agent-console-bar">
        <div className="agent-console-copy">
          <div className="agent-console-kicker"><SafetyCertificateOutlined /> 旭日之战代理工作区</div>
          <div className="agent-console-title">
            {canManageSubAgents ? '团队、玩家、流水分开展示，代理端不混用主后台配置权限。' : '玩家和流水分开展示，3级代理不展示下级管理入口。'}
          </div>
          <div className="agent-console-subtitle">当前账号只能查看和管理自身权限范围内的数据；越级访问会在服务端直接拦截。</div>
        </div>
        <div className="agent-console-actions">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadAgentData();
              if (canManageSubAgents) {
                loadSubAgents(subAgentPagination.current, subAgentKeyword, subAgentPagination.pageSize);
                loadSubAgentStats();
              }
              if (activeTab === 'recharge') {
                loadRechargeLogs({
                  page: rechargePagination.current,
                  pageSize: rechargePagination.pageSize,
                  keyword: rechargeQuery.keyword,
                  agentId: rechargeQuery.agentId,
                  dateStart: rechargeRange[0],
                  dateEnd: rechargeRange[1]
                });
              }
            }}
          >
            刷新数据
          </Button>
        </div>
      </div>

      <Card className="section-card">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {canManageSubAgents && (
        <Modal
          title={<span><PlusOutlined style={{ marginRight: 8, color: '#667eea' }} />创建{childRoleName}</span>}
          open={createVisible}
          onCancel={() => setCreateVisible(false)}
          onOk={() => createForm.submit()}
          width={600}
          okText="创建"
          cancelText="取消"
        >
          <Alert
            message={`创建${childRoleName}`}
            description={agentLevel === 1
              ? '2级总代将使用独立账号登录后台，可管理自己的用户和3级代理；parent_id 会自动设为当前账号。'
              : '3级代理将使用独立账号登录后台，只能管理自己的用户和收益；parent_id 会自动设为当前账号。'}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form form={createForm} layout="vertical" onFinish={handleCreateSubAgent}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="username"
                  label="登录账号"
                  rules={[
                    { required: true, message: '请输入登录账号' },
                    { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线' }
                  ]}
                >
                  <Input placeholder="请输入登录账号" prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="初始密码"
                  rules={[
                    { required: true, message: '请输入初始密码' },
                    { min: 6, message: '密码长度不能少于6位' }
                  ]}
                  extra="至少6位字符"
                >
                  <Input.Password placeholder="请输入初始密码" prefix={<LockOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="second_password"
                  label="二级密码"
                  extra="明文存储，用于代理修改密码时二次确认"
                >
                  <Input.Password placeholder="请输入二级密码（可选）" prefix={<LockOutlined />} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="agent_name"
                  label="代理名称"
                  rules={[{ required: true, message: '请输入代理名称' }]}
                >
                  <Input placeholder="请输入代理名称" prefix={<CrownOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="invite_code"
                  label="邀请码"
                  rules={[{ required: true, message: '请输入邀请码' }]}
                  extra={
                    <Button size="small" type="link" onClick={handleGenerateCode} style={{ padding: 0 }}>
                      自动生成
                    </Button>
                  }
                >
                  <Input placeholder="请输入邀请码或点击自动生成" prefix={<KeyOutlined />} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contact" label="联系方式">
                  <Input placeholder="请输入联系方式" prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="commission_rate" label="佣金比例" extra="百分比，如 10 表示 10%">
                  <Input type="number" placeholder="请输入佣金比例" prefix={<DollarOutlined />} suffix="%" min={0} max={100} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="remark" label="备注">
              <Input.TextArea placeholder="请输入备注" rows={2} />
            </Form.Item>
          </Form>
        </Modal>
      )}

      <Modal
        title={<span><EditOutlined style={{ marginRight: 8, color: '#faad14' }} />编辑下级代理</span>}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => editForm.submit()}
        width={600}
        okText="保存"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_name"
                label="代理名称"
                rules={[{ required: true, message: '请输入代理名称' }]}
              >
                <Input placeholder="请输入代理名称" prefix={<CrownOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="invite_code"
                label="邀请码"
                rules={[{ required: true, message: '请输入邀请码' }]}
              >
                <Input placeholder="请输入邀请码" prefix={<KeyOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact" label="联系方式">
                <Input placeholder="请输入联系方式" prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="commission_rate" label="佣金比例" extra="百分比，如 10 表示 10%">
                <Input type="number" placeholder="请输入佣金比例" prefix={<DollarOutlined />} suffix="%" min={0} max={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="重置密码"
                extra="留空则不修改，填写则重置为新密码"
                rules={[{ min: 6, message: '密码长度不能少于6位' }]}
              >
                <Input.Password placeholder="留空不修改" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="second_password"
                label="二级密码"
                extra="明文存储，用于代理修改密码时二次确认"
              >
                <Input.Password placeholder="请输入二级密码（可选）" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="请输入备注" rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span><EyeOutlined style={{ marginRight: 8, color: '#1890ff' }} />下级代理详情</span>}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {subAgentDetail && (
          <div>
            <Descriptions title="基本信息" column={2} bordered size="middle" labelStyle={{ fontWeight: 600 }}>
              <Descriptions.Item label="代理ID">{subAgentDetail.agent.id}</Descriptions.Item>
              <Descriptions.Item label="代理名称"><Text strong>{subAgentDetail.agent.agent_name}</Text></Descriptions.Item>
              <Descriptions.Item label="登录账号"><Text code>{subAgentDetail.agent.username}</Text></Descriptions.Item>
              <Descriptions.Item label="当前层级">{subAgentDetail.agent.agent_role_name || getAgentRoleName(getAgentLevel(subAgentDetail.agent))}</Descriptions.Item>
              <Descriptions.Item label="邀请码">
                <Space size={4}>
                  <Text code>{subAgentDetail.agent.invite_code}</Text>
                  <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopyCode(subAgentDetail.agent.invite_code)} title="复制邀请码" style={{ padding: '0 2px' }} />
                  <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => handleCopyRegisterLink(subAgentDetail.agent.invite_code)} title="复制注册链接" style={{ padding: '0 2px' }} />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="联系方式">{subAgentDetail.agent.contact || '-'}</Descriptions.Item>
              <Descriptions.Item label="佣金比例">
                <Tag color="purple">{subAgentDetail.agent.commission_rate || 0}%</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={subAgentDetail.agent.status === 1 ? 'green' : 'red'}>
                  {subAgentDetail.agent.status === 1 ? '活跃' : '禁用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {subAgentDetail.agent.create_time ? new Date(subAgentDetail.agent.create_time).toLocaleString('zh-CN') : '-'}
              </Descriptions.Item>
              {subAgentDetail.agent.remark && (
                <Descriptions.Item label="备注" span={2}>{subAgentDetail.agent.remark}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">绑定用户 ({subAgentDetail.users?.length || 0})</Divider>
            <Table
              columns={[
                { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100, render: (t) => <Text code>{t}</Text> },
                { title: '用户名', dataIndex: 'user_name', key: 'user_name', width: 100 },
                { title: '昵称', dataIndex: 'nick_name', key: 'nick_name', width: 100, render: (t) => t || '-' },
                {
                  title: '所属代理',
                  dataIndex: 'source_agent_name',
                  key: 'source_agent_name',
                  width: 130,
                  render: (name, record) => name || `代理ID ${record.source_agent_id}`
                },
                { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (l) => l ? <Tag color="blue">Lv.{l}</Tag> : '-' },
                { title: '绑定时间', dataIndex: 'bind_time', key: 'bind_time', width: 160, render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-' }
              ]}
              dataSource={subAgentDetail.users || []}
              rowKey={(record) => `${record.server_id || 0}-${record.user_id || 0}-${record.game_user_id || 0}`}
              pagination={{ pageSize: 5, showTotal: (total) => `共 ${total} 个用户` }}
              size="small"
              locale={{ emptyText: <Empty description="暂无绑定用户" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </div>
        )}
      </Modal>

      {/* 人工代充: 转入下级 */}
      {isRechargeAgent && (
        <Modal
          title={<span><SwapOutlined style={{ marginRight: 8 }} />转余额给下级代理</span>}
          open={transferVisible}
          onCancel={() => setTransferVisible(false)}
          onOk={() => transferForm.submit()}
          okText="确认转账"
          cancelText="取消"
          confirmLoading={transferSubmitting}
          width={520}
          destroyOnClose
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="仅可转给你的下级代理（全部后代）。转账在同一事务内从你余额借出、向对方贷入，可安全重试(幂等)。"
          />
          <Form form={transferForm} layout="vertical" onFinish={handleTransferSubmit}>
            <Form.Item
              name="toAgentId"
              label="下级代理ID"
              rules={[{ required: true, message: '请输入下级代理ID' }]}
              extra={mySubAgents.length > 0
                ? `直属下级参考：${mySubAgents.slice(0, 8).map((s) => `${s.agent_name}(ID ${s.id})`).join('，')}`
                : '请输入你团队内下级代理的ID'}
            >
              <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="下级代理的数字ID" />
            </Form.Item>
            <Form.Item name="amount" label="转账金额(¥)" rules={[{ required: true, message: '请输入金额' }]}>
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="正数" />
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea rows={2} maxLength={255} placeholder="可选" />
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 补单(reissue): 客服凭账号/账号ID 直接补发平台币并计入业绩。受控弹窗, 总价随数量实时重算。 */}
      {isRechargeAgent && (
        <Modal
          title={<span><PlusOutlined style={{ marginRight: 8 }} />补单（人工补发平台币）</span>}
          open={reissueVisible}
          onCancel={() => setReissueVisible(false)}
          onOk={handleReissueSubmit}
          okText="确定"
          cancelText="取消"
          confirmLoading={reissueSubmitting}
          width={520}
          destroyOnClose
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="凭账号或账号ID为玩家补发平台币, 落一笔已完成订单并计入该玩家所属代理业绩。玩家实际到账为“数量×1.03”(多送3%, 由你的余额承担); 业绩金额按商品总价(数量)计。"
          />
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>补单方式</div>
            <Radio.Group
              value={reissueMode}
              onChange={(e) => { setReissueMode(e.target.value); setReissueIdentifier(''); }}
            >
              <Radio value="account">按账号</Radio>
              <Radio value="accountId">按账号ID</Radio>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>{reissueMode === 'accountId' ? '账号ID' : '账号'}</div>
            <Input
              value={reissueIdentifier}
              onChange={(e) => setReissueIdentifier(e.target.value)}
              maxLength={64}
              placeholder={reissueMode === 'accountId' ? '请输入玩家账号ID(与用户中心/客服对话框一致)' : '请输入玩家账号'}
            />
          </div>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>商品名称</div>
              <Input value="平台币" disabled />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>商品单价</div>
              <InputNumber value={1} disabled style={{ width: '100%' }} />
            </Col>
          </Row>
          <Row gutter={12} style={{ marginTop: 12 }}>
            <Col span={12}>
              <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>购买数量</div>
              <InputNumber
                value={reissueQuantity}
                onChange={(v) => setReissueQuantity(v)}
                min={1}
                step={1}
                precision={0}
                style={{ width: '100%' }}
                placeholder="整数、大于0"
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>商品总价</div>
              <InputNumber
                value={reissueQuantity ? reissueTotalPrice : null}
                disabled
                style={{ width: '100%' }}
                placeholder="商品总价将自动计算"
              />
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                多送3%后实发 {reissueQuantity ? (reissueTotalPrice * 1.03).toFixed(2) : '—'} 平台币
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 4, color: '#64748b', fontSize: 13 }}>备注(可选)</div>
            <Input.TextArea
              value={reissueRemark}
              onChange={(e) => setReissueRemark(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="可选"
            />
          </div>
        </Modal>
      )}

      {/* 需求2.2: 单玩家订单明细(全部状态) */}
      {isRechargeAgent && (
        <Modal
          title={(
            <span>
              <EyeOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              {currentServedPlayer
                ? `${currentServedPlayer.accountName || '玩家'}（账号ID:${currentServedPlayer.accountId}）的订单明细`
                : '玩家订单明细'}
            </span>
          )}
          open={servedOrdersVisible}
          onCancel={() => setServedOrdersVisible(false)}
          footer={null}
          width={980}
        >
          <Table
            columns={servedOrderColumns}
            dataSource={servedOrders}
            loading={servedOrdersLoading}
            rowKey={(record) => String(record.sessionNo || record.orderNo)}
            pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 单` }}
            size="small"
            scroll={{ x: 1360 }}
            locale={{ emptyText: renderEmptyPanel('暂无订单', '该玩家在你名下暂无订单记录。') }}
          />
        </Modal>
      )}
    </div>
  );
}

export default AgentDashboard;
